import { Select, SelectContent, SelectItem, SelectTrigger } from '@HoudiniUI/Select';
import { brandPrimary } from '@Styles/colors';
import { baseStyleSheet } from '@Styles/styles';
import * as Api from '@ViewModels';
import { ISelectOption, MultiSelect } from '@WebComponents/DeprecatedSelect';
import { SimpleTagSelector } from '@WebComponents/entities/tags/SimpleTagSelector';
import { Modal } from '@WebComponents/Modal';
import { ClipboardIcon } from '@WebComponents/svgs/icons/ClipboardIcon';
import { EditItemPenIcon } from '@WebComponents/svgs/icons/EditItemPenIcon';
import { IntegrationIcon } from '@WebComponents/svgs/icons/IntegrationIcon';
import { MergeIcon } from '@WebComponents/svgs/icons/MergeIcon';
import { SuccessCheckIcon } from '@WebComponents/svgs/icons/SuccessCheckIcon';
import { UnmergeIcon } from '@WebComponents/svgs/icons/UnmergeIcon';
import { TextInput } from '@WebComponents/TextInput';
import { TextInputModal } from '@WebComponents/TextInputModal';
import { css, StyleDeclarationValue } from 'aphrodite';
import produce from 'immer';
import { makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { AccountDetailsInfoField } from '../../../../admin/components/accountDetails/AccountDetailsInfoField';
import { ConditionalAccess } from '../../../../admin/components/ConditionalAccess';
import { AdminAccountIntegrationsViewModel } from '../../../../admin/viewModels';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { getDisplayName } from '../../../../models/NameUtils';
import { copyToClipboard, getEntityDisplayName } from '../../../../models/UiUtils';
import { AccountUsersViewModel, AccountUserViewModel, AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { IntegrationsSyncingLoader } from '../AdminIntegrationsSyncingLoader';
import { ConfigurableIntegrationSettingsContext, IConfigurableIntegrationSettingsContext } from './context';
import {
	AdminConfigurableIntegrationSettingsHeader,
	AdminConfigurableIntegrationSettingsIcon,
	AdminConfigurableIntegrationSettingsSetupSteps,
	getDefaultOwnershipRoleForIntegrationType,
} from './presentation';
import { styleSheet } from './styles';

interface IProps extends IUserSessionComponentProps, IErrorMessageComponentProps, IImpersonationContextComponentProps {
	accountId?: string;
	className?: string;
	integration?: Api.IConfigurableIntegration;
	integrationName: string;
	integrationType: Api.ConfigurableIntegrationType;
	isLoading?: boolean;
	onRenderMapping?(): React.ReactNode;
	onRenderRecentActivity?(): React.ReactNode;
	onRequestRecentActivityReload?(): Promise<any>;
	onResyncLocal?(): void;
	onSignIn?(integration: Partial<Api.IConfigurableIntegration>): void;
	onUnmergeContacts?(): void;
	onUpdateIntegrationSettings(fieldsToUpdate: Partial<Api.IAms360Integration | Api.IHawkSoftIntegration>): void;
	styles?: StyleDeclarationValue[];
	onMapByResourceId?(): void;
	canUpdateMappingKey?: boolean;
}

interface IState {
	agencyId: string;
	amsFieldsToUpdate: Partial<Api.IAms360Integration>;
	clioFieldsToUpdate: Partial<Api.IClioIntegration>;
	context?: IConfigurableIntegrationSettingsContext;
	domainName?: string;
	eclipseFieldsToUpdate: Partial<Api.IEclipseIntegration>;
	xanatekFieldsToUpdate: Partial<Api.IGeneralCsvIntegration>;
	epicFieldsToUpdate: Partial<Api.IEpicCsvIntegration>;
	ezLynxFieldsToUpdate: Partial<Api.IEzLynxCsvIntegration>;
	hawkSoftFieldsToUpdate: Partial<Api.IHawkSoftIntegration>;
	hubSpotFieldsToUpdate: Partial<Api.IHubSpotIntegration>;
	isEditing?: boolean;
	isUserCredentialsModalOpened?: boolean;
	qqFieldsToUpdate: Partial<Api.IQQIntegration>;
	redtailFieldsToUpdate: Partial<Api.IRedtailIntegration>;
	sharedFieldsToUpdate: Partial<Api.IConfigurableIntegration>;
	showNumberOfYearsModal: boolean;
	showSegmentTagModal: boolean;
	showSubStatusNumberOfYearsModal: boolean;
	cancelledYears: number;
	donorPerfectFieldsToUpdate: Partial<Api.IDonorPerfectIntegration>;
}

const ProspectOptionsOptions: ISelectOption<Api.ProspectIntegration>[] = [
	{
		dataContext: Api.ProspectIntegration.All,
		id: 'prospect-option-all',
		text: 'All',
	},
	{
		dataContext: Api.ProspectIntegration.None,
		id: 'prospect-option-none',
		text: 'None',
	},
	{
		dataContext: Api.ProspectIntegration.Timeline,
		id: 'prospect-option-define-timeline',
		text: 'Define Timeline',
	},
];
const RedtailOwnershipRoleOptions: ISelectOption<Api.RedtailIntegrationOwnershipRole>[] = [
	{
		dataContext: Api.RedtailIntegrationOwnershipRole.FirstObservedUserFromContact,
		id: 'ownership-option-first-observed-user',
		text: 'First Observed User',
	},
	{
		dataContext: Api.RedtailIntegrationOwnershipRole.ServicingAdvisor,
		id: 'ownership-option-servicing-advisor',
		text: 'Servicing Advisor',
	},
	{
		dataContext: Api.RedtailIntegrationOwnershipRole.ServicingAdvisorRestricted,
		id: 'ownership-option-servicing-advisor-restricted',
		text: 'Servicing Advisor (Mapped Only)',
	},
	{
		dataContext: Api.RedtailIntegrationOwnershipRole.WritingAdvisor,
		id: 'ownership-option-writing-advisor',
		text: 'Writing Advisor',
	},
	{
		dataContext: Api.RedtailIntegrationOwnershipRole.WritingAdvisorRestricted,
		id: 'ownership-option-writing-adivsor-restricted',
		text: 'Writing Advisor (Mapped Only)',
	},
];
const ClioOwnershipRoleOptions: ISelectOption<Api.ClioIntegrationOwnershipRole>[] = [
	{
		dataContext: Api.ClioIntegrationOwnershipRole.ResponsibleAttorney,
		id: 'ownership-option-responsible-attorney',
		text: 'Responsible Attorney',
	},
	{
		dataContext: Api.ClioIntegrationOwnershipRole.OriginatingAttorney,
		id: 'ownership-option-originating-attorney',
		text: 'Originating Attorney',
	},
];
const DonorPerfectOwnershipRoleOptions: ISelectOption<Api.DonorPerfectIntegrationOwnershipRole>[] = [
	{
		dataContext: Api.DonorPerfectIntegrationOwnershipRole.DefaultOwner,
		id: 'ownership-option-default-owner',
		text: 'Default Owner',
	},
];
const ClioAttorneyTagOptions: ISelectOption<Api.AttorneyTagOption>[] = [
	{
		dataContext: Api.AttorneyTagOption.None,
		id: 'attorney-tag-option-none',
		text: 'None',
	},
	{
		dataContext: Api.AttorneyTagOption.MostRecent,
		id: 'attorney-tag-option-most-recent',
		text: 'Most Recent',
	},
	{
		dataContext: Api.AttorneyTagOption.All,
		id: 'attorney-tag-option-all',
		text: 'All',
	},
];
const OwnershipRoleOptions: ISelectOption<boolean>[] = [
	{
		dataContext: true,
		id: 'ownership-option-both',
		text: 'Both Producer & CSRs',
	},
	{
		dataContext: false,
		id: 'ownership-option-just-ownership',
		text: 'Just Ownership',
	},
];
const BirthdayKeyFactsOptions: ISelectOption<Api.BirthdayKeyFactDisplay>[] = [
	{
		dataContext: Api.BirthdayKeyFactDisplay.All,
		id: 'birthday-keyfact-option-all',
		text: 'All contacts',
	},
	{
		dataContext: Api.BirthdayKeyFactDisplay.ClientOnly,
		id: 'birthday-keyfact-option-clients-only',
		text: 'Clients only',
	},
	{
		dataContext: Api.BirthdayKeyFactDisplay.ProspectOnly,
		id: 'birthday-keyfact-option-prospects-only',
		text: 'Prospects only',
	},
];
const FinancialReviewOptions: ISelectOption<Api.FinancialReviewKeyFactOptions | 'Disabled'>[] = [
	{
		dataContext: Api.FinancialReviewKeyFactOptions.Birthdate,
		id: 'financial-review-option-birth-date',
		text: 'Birth Date',
	},
	{
		dataContext: Api.FinancialReviewKeyFactOptions.ClientSince,
		id: 'financial-review-option-client-since',
		text: 'Client Since',
	},
	{
		dataContext: Api.FinancialReviewKeyFactOptions.ClientReview,
		id: 'financial-review-option-client-review',
		text: 'Client Review',
	},
	{
		dataContext: 'Disabled',
		id: 'financial-review-option-disable',
		text: 'Disable',
	},
];

const BaseMappingOptions: ISelectOption<Api.BaseInsuranceIntegrationOwnershipRole>[] = [
	{
		dataContext: Api.BaseInsuranceIntegrationOwnershipRole.Producer,
		id: 'mapping-option-producer',
		text: 'Producer',
	},
	{
		dataContext: Api.BaseInsuranceIntegrationOwnershipRole.CSR,
		id: 'mapping-option-csr',
		text: 'CSR',
	},
];
const AmsMappingOptions: ISelectOption<Api.Ams360IntegrationOwnershipRole>[] = [
	{
		dataContext: Api.Ams360IntegrationOwnershipRole.Executive,
		id: 'mapping-option-executive',
		text: 'Executive',
	},
	{
		dataContext: Api.Ams360IntegrationOwnershipRole.Representative,
		id: 'mapping-option-representative',
		text: 'Representative',
	},
];

const CommercialProfileOptions: ISelectOption<Api.CommercialProfiles>[] = [
	{
		dataContext: Api.CommercialProfiles.All,
		id: 'commercial-profile-option-all',
		text: 'All',
	},
	{
		dataContext: Api.CommercialProfiles.PrimaryOnly,
		id: 'commercial-profile-option-primary-only',
		text: 'Primary Only',
	},
	{
		dataContext: Api.CommercialProfiles.PrimarySecondaryOnly,
		id: 'commercial-profile-option-primary-and-secondary-only',
		text: 'Primary and Secondary Only',
	},
];

const PersonalProfileOptions: ISelectOption<Api.PersonalProfiles>[] = [
	{
		dataContext: Api.PersonalProfiles.PrimaryOnly,
		id: 'personal-profile-option-primary-only',
		text: 'Primary Only',
	},
	{
		dataContext: Api.PersonalProfiles.PrimarySecondaryOnly,
		id: 'personal-profile-option-primary-and-secondary-only',
		text: 'Primary and Secondary Only',
	},
];

const LifeHealthProfileOptions: ISelectOption<Api.LifeHealthProfiles>[] = [
	{
		dataContext: Api.LifeHealthProfiles.PrimaryOnly,
		id: 'lifehealth-profile-option-primary-only',
		text: 'Primary Only',
	},
	{
		dataContext: Api.LifeHealthProfiles.PrimarySecondaryOnly,
		id: 'lifehealth-profile-option-primary-and-secondary-only',
		text: 'Primary and Secondary Only',
	},
];

const EnableOptions: ISelectOption<boolean>[] = [
	{
		dataContext: true,
		id: 'enable-option-enable',
		text: 'Enable',
	},
	{
		dataContext: false,
		id: 'enable-option-disable',
		text: 'Disable',
	},
];
const SegmentTagOptions: ISelectOption<boolean>[] = [
	{
		dataContext: true,
		id: 'enable-option-enable',
		text: 'Multiple Selected',
	},
	{
		dataContext: false,
		id: 'enable-option-disable',
		text: 'None Selected',
	},
];

const dateFormat = 'L LT';

const WriteBackOptions: ISelectOption<Api.WriteBackOption>[] = [
	{
		dataContext: Api.WriteBackOption.Email,
		id: 'write-back-option-email',
		text: 'Email',
		type: 'checkbox',
	},
	{
		dataContext: Api.WriteBackOption.Note,
		id: 'write-back-option-note',
		text: 'Note',
		type: 'checkbox',
	},
	{
		dataContext: Api.WriteBackOption.PhoneCall,
		id: 'write-back-option-phone-call',
		text: 'Phone Call',
		type: 'checkbox',
	},
	{
		dataContext: Api.WriteBackOption.TextMessage,
		id: 'write-back-option-text-message',
		text: 'Text Message',
		type: 'checkbox',
	},
	{
		dataContext: Api.WriteBackOption.HandwrittenCard,
		id: 'write-back-option-handwritten-card',
		text: 'Handwritten Card',
		type: 'checkbox',
	},
];

type TagKeyFactOptionKey =
	| keyof Api.IAms360TagKeyFactOptions
	| keyof Api.IHawkSoftTagKeyFactOptions
	| keyof Api.IRedtailTagKeyFactOptions
	| keyof Api.IClioTagKeyFactOptions
	| keyof Api.IEpicTagKeyFactOptions
	| keyof Api.IQQTagKeyFactOptions
	| keyof Api.IEzLynxTagKeyFactOptions
	| keyof Api.IDonorPerfectTagKeyFactOptions;

type RedtailAvailableOptions = 'statuses' | 'categories' | 'contactTypes';

interface ITagKeyFactOption {
	enabled: boolean;
	tagName: TagKeyFactOptionKey;
}

class _AdminConfigurableIntegrationSettings extends React.Component<IProps, IState> {
	private mAccountUsers: AccountUsersViewModel;
	private mIntegrationsVM: AdminAccountIntegrationsViewModel;

	constructor(props: IProps) {
		super(props);

		makeObservable<_AdminConfigurableIntegrationSettings, 'mAccountUsers' | 'mIntegrationsVM'>(this, {
			mAccountUsers: observable.ref,
			mIntegrationsVM: observable.ref,
		});

		const { integrationName, integrationType, integration, accountId, userSession } = this.props;
		this.mAccountUsers = new AccountUsersViewModel(userSession, {
			id: accountId,
		});
		this.mIntegrationsVM = new AdminAccountIntegrationsViewModel(userSession, {
			id: accountId,
		});
		this.state = {
			agencyId: '',
			amsFieldsToUpdate: {},
			clioFieldsToUpdate: {},
			context: {
				accountId,
				integration,
				integrationName,
				integrationType,
				onOwnerShipRoleChanged: this.onOwnershipRoleChanged,
				ownershipRole: getDefaultOwnershipRoleForIntegrationType(integrationType),
			},
			domainName: '',
			eclipseFieldsToUpdate: {},
			epicFieldsToUpdate: {},
			ezLynxFieldsToUpdate: {},
			hawkSoftFieldsToUpdate: {},
			hubSpotFieldsToUpdate: {},
			isEditing: false,
			qqFieldsToUpdate: {},
			redtailFieldsToUpdate: {},
			sharedFieldsToUpdate: {},
			showNumberOfYearsModal: false,
			showSegmentTagModal: false,
			showSubStatusNumberOfYearsModal: false,
			xanatekFieldsToUpdate: {},
			donorPerfectFieldsToUpdate: {},
			cancelledYears: undefined,
		};
	}

	public componentDidMount() {
		const { errorMessages } = this.props;
		this.mAccountUsers.loadUsers()?.catch((err: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(err);
		});
		this.loadTags();
	}

	public render() {
		const { className, integration, integrationType, isLoading, styles } = this.props;
		const { context } = this.state;

		const userInfoReady =
			integrationType === Api.ConfigurableIntegrationType.Redtail
				? !!(integration as Api.IRedtailIntegration).redtailUserConfigurations
				: integrationType === Api.ConfigurableIntegrationType.Clio
					? !!(integration as Api.IClioIntegration).integrationUserConfigurations
					: !!integration.userMap;
		return (
			<ConfigurableIntegrationSettingsContext.Provider value={context}>
				<div
					className={`${css(styleSheet.container, ...(styles || []))} configurable-integration-settings ${
						className || ''
					}`}
				>
					<AdminConfigurableIntegrationSettingsHeader />
					{isLoading ||
					(integration.isConnected && !userInfoReady && integrationType !== Api.ConfigurableIntegrationType.Eclipse)
						? this.renderLoading()
						: integration.isConnected ||
								integrationType === Api.ConfigurableIntegrationType.Redtail ||
								integrationType === Api.ConfigurableIntegrationType.Clio ||
								integrationType === Api.ConfigurableIntegrationType.QQ ||
								integrationType === Api.ConfigurableIntegrationType.DonorPerfect
							? this.renderDone()
							: this.renderSetupSettings()}
				</div>
			</ConfigurableIntegrationSettingsContext.Provider>
		);
	}

	private renderSetupSettings() {
		const { agencyId } = this.state;
		return (
			<div>
				<AdminConfigurableIntegrationSettingsSetupSteps />
				<div className={css(styleSheet.card)}>
					<AdminConfigurableIntegrationSettingsIcon styles={[styleSheet.cardImage]} />
					<div className={css(styleSheet.labelText)}>Agency ID</div>
					<TextInput
						className={css(styleSheet.textInput)}
						inputId='agency-id'
						onChange={this.onChangeAgencyId}
						type='text'
						value={agencyId}
					/>
					<button className={css([baseStyleSheet.ctaButton, styleSheet.button])} onClick={this.onSignIn}>
						<span>Sign In</span>
					</button>
				</div>
			</div>
		);
	}

	private renderLoading() {
		return (
			<div className={css(styleSheet.loadingSpinner)}>
				<div className={css(styleSheet.syncText)}>
					We were able to find the data associated with your agency. You may start syncing the data. This could take a
					while, depending on the size of the company and how many clients you have.
				</div>
				<IntegrationsSyncingLoader />
			</div>
		);
	}

	private renderDone() {
		const { integration, integrationType, onRenderRecentActivity, onRequestRecentActivityReload } = this.props;
		let numRows = 3;
		const cols: JSX.Element[] = [];
		let col: JSX.Element[] = [];
		let settings: JSX.Element[] = [];

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			numRows = 4;
			settings = [
				this.renderIDs(),
				this.renderMappingKey(),
				this.renderLastExtractDate(),
				this.renderLastLoadDate(),
				this.renderMappingBy(),
				this.renderOwnershipTag(),
				this.renderLastNotificationReceived(),
				this.renderBirthdayKeyFacts(),
				this.renderAms360UserCredentials(),
				this.renderProspectOptionsField(),
				this.renderWriteBack(),
				this.renderCommercialProfiles(),
				this.renderPersonalProfiles(),
				this.renderTagsDropdown(),
				this.renderImportInactiveClients(),
				this.renderPrimaryEmailOnly(),
				this.renderSegmentByTags(),
			];
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			numRows = 4;
			settings = [
				this.renderIDs(),
				this.renderMappingKey(),
				this.renderLastExtractDate(),
				this.renderLastLoadDate(),
				this.renderMappingBy(),
				this.renderOwnershipTag(),
				this.renderBirthdayKeyFacts(),
				this.renderProspectOptionsField(),
				this.renderWriteBack(),
				this.renderCommercialProfiles(),
				this.renderPersonalProfiles(),
				this.renderLifeHealthProfiles(),
				this.renderTagsDropdown(),
				this.renderHawksoftSubStatus(),
				this.renderPrimaryEmailOnly(),
				this.renderSegmentByTags(),
			];
		} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			numRows = 4;
			settings = [
				this.renderMappingKey(),
				this.renderLastExtractDate(),
				this.renderLastLoadDate(),
				this.renderMappingBy(),
				this.renderBirthdayKeyFacts(),
				this.renderFinancialReviewKeyFacts(),
				this.renderProspectOptionsField(),
				this.renderWriteBack(),
				this.renderTagsDropdown(),
				this.renderPrimaryUserId(),
				this.renderPrimaryEmailOnly(),
				this.renderUseForMeetingScheduling(),
				this.renderSegmentByTags(),
			];
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			numRows = 4;
			settings = [
				this.renderIDs(),
				this.renderMappingKey(),
				this.renderLastExtractDate(),
				this.renderLastLoadDate(),
				this.renderMappingBy(),
				this.renderOwnershipTag(),
				this.renderBirthdayKeyFacts(),
				this.renderProspectOptionsField(),
				this.renderPrimaryEmailOnly(),
				this.renderCommercialProfiles(),
				this.renderPersonalProfiles(),
				this.renderTagsDropdown(),
				this.renderCopyApiToken(),
				this.renderSalutationAsFirstName(),
				this.renderSegmentByTags(),
			];
		} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			numRows = 4;
			settings = [
				this.renderMappingKey(),
				this.renderLastExtractDate(),
				this.renderLastLoadDate(),
				this.renderClioOwnershipTag(),
				this.renderBirthdayKeyFacts(),
				this.renderWriteBack(),
				this.renderTagsDropdown(),
				this.renderClioAttorneyTagSettings(Api.ClioIntegrationOwnershipRole.OriginatingAttorney),
				this.renderClioAttorneyTagSettings(Api.ClioIntegrationOwnershipRole.ResponsibleAttorney),
				this.renderPrimaryUserId(),
				this.renderNonClients(),
				this.renderRelatedContacts(),
				this.renderPrimaryEmailOnly(),
				this.renderSegmentByTags(),
			];
		} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
			settings = [
				this.renderIDs(),
				this.renderMappingKey(),
				this.renderLastExtractDate(),
				this.renderLastLoadDate(),
				this.renderBirthdayKeyFacts(),
				this.renderProspectOptionsField(),
				this.renderTagsDropdown(),
				this.renderReportEmailAddress(),
				this.renderPrimaryEmailOnly(),
				this.renderIgnoreNicknames(),
				this.renderSegmentByTags(),
			];
		} else if (Api.isGeneralCsvIntegration(integrationType)) {
			numRows = 4;
			settings = [
				this.renderMappingKey(),
				this.renderLastExtractDate(),
				this.renderLastLoadDate(),
				this.renderMappingBy(),
				this.renderBirthdayKeyFacts(),
				this.renderProspectOptionsField(),
				this.renderTagsDropdown(),
				this.renderCopyApiToken(),
				this.renderPrimaryEmailOnly(),
				this.renderIgnoreNicknames(),
				this.renderSegmentByTags(),
			];
		} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
			settings = [
				this.renderIDs(),
				this.renderMappingKey(),
				this.renderLastExtractDate(),
				this.renderLastLoadDate(),
				this.renderMappingBy(),
				this.renderBirthdayKeyFacts(),
				this.renderProspectOptionsField(),
				this.renderTagsDropdown(),
				this.renderReportEmailAddress(),
				this.renderPrimaryEmailOnly(),
				this.renderIgnoreNicknames(),
				this.renderSegmentByTags(),
			];
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			settings = [
				this.renderMappingKey(),
				this.renderLastExtractDate(),
				this.renderLastLoadDate(),
				this.renderBirthdayKeyFacts(),
				this.renderMappingBy(),
				this.renderOwnershipTag(),
				this.renderProspectOptionsField(),
				this.renderWriteBack(),
				this.renderCommercialProfiles(),
				this.renderTagsDropdown(),
				this.renderPrimaryEmailOnly(),
				this.renderSegmentByTags(),
			];
		} else if (integrationType === Api.ConfigurableIntegrationType.HubSpot) {
			settings = [this.renderMappingKey(), this.renderPrimaryEmailOnly()];
		} else if (integrationType === Api.ConfigurableIntegrationType.DonorPerfect) {
			settings = [
				this.renderMappingKey(),
				this.renderLastExtractDate(),
				this.renderLastLoadDate(),
				this.renderPrimaryEmailOnly(),
				this.renderMappingBy(),
				this.renderTagsDropdown(),
				this.renderSegmentByTags(),
			];
		}

		for (let i = 0; i < settings.length; i++) {
			/**
			 * Need to manually set height to ensure all settings are aligned because flexbox was not able to accomplish while
			 * taking into account differing heights of setting containers and partial columns at same time.
			 */
			if (settings[i] !== null) {
				col.push(
					<div key={uuid()} style={{ minHeight: `${100 / numRows}%` }}>
						{settings[i]}
					</div>
				);
			}

			if (col.length === numRows || i === settings.length - 1) {
				cols.push(this.renderCol(col));
				col = [];
			}
		}

		return (
			<div>
				<section className={css(styleSheet.section)}>
					<div className={css(styleSheet.sectionTitle)}>Integration settings</div>
					{integration.locks?.ownershipRoleLock && (
						<div className={css(styleSheet.error)}>
							Sorry, this integration cannot be edited until after syncing is complete. Please refresh this page to
							check for updates.
						</div>
					)}
					<div className={css(styleSheet.cardGroup)}>
						{cols}
						{this.renderEditPen()}
					</div>
					{this.renderRedtailAvailableOptionSection()}
				</section>
				{onRenderRecentActivity && (
					<section className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionTitle)}>
							<div>Recent activity</div>
							{onRenderRecentActivity && onRequestRecentActivityReload && (
								<button onClick={this.reloadRecentActivity()} title='Reload activity'>
									<IntegrationIcon fill={brandPrimary} />
								</button>
							)}
						</div>
						{onRenderRecentActivity()}
					</section>
				)}
				<section className={css(styleSheet.section)}>
					<div className={css(styleSheet.sectionTitle)}>User mapping</div>
					{this.renderMappingInitials()}
					{this.renderUsersMapping()}
					{this.renderAms360LoginModal()}
				</section>
			</div>
		);
	}

	private reloadRecentActivity = () => () => {
		this.props.onRequestRecentActivityReload?.();
	};

	private onNumberOfYearsSelected = (result?: string, cancel?: boolean) => {
		const timelineYears = parseInt(result, 10);
		if (!cancel && result && !isNaN(timelineYears)) {
			this.setState({
				sharedFieldsToUpdate: {
					...this.state.sharedFieldsToUpdate,
					prospectOptions: {
						prospectIntegration: Api.ProspectIntegration.Timeline,
						timelineYears,
					},
				},
				showNumberOfYearsModal: false,
			});
		} else if (cancel || !result) {
			this.setState({ showNumberOfYearsModal: false });
		} else if (isNaN(timelineYears)) {
			toast.error('The number you supplied does not appear to be formatted correctly');
		}
	};

	private onNumberOfSubStatusYearsSelected = (result?: string, cancel?: boolean) => {
		if (!cancel && result) {
			const nextState = produce(this.state, draft => {
				const parsed = parseInt(result, 10);

				draft.hawkSoftFieldsToUpdate = draft.hawkSoftFieldsToUpdate || {};
				draft.hawkSoftFieldsToUpdate.tagKeyFactOptions = draft.hawkSoftFieldsToUpdate.tagKeyFactOptions || {};
				draft.hawkSoftFieldsToUpdate.tagKeyFactOptions.subStatusTagOption = {
					...(draft.hawkSoftFieldsToUpdate.tagKeyFactOptions.subStatusTagOption || {}),
					cancelledYears: parsed,
				};
				draft.showSubStatusNumberOfYearsModal = false;
				draft.cancelledYears = parsed;
			});

			this.setState(nextState);
		} else if (cancel || !result) {
			this.setState({ showSubStatusNumberOfYearsModal: false });
		}
	};

	private isNumberOfYearsValid = (value: string): boolean => {
		if (value && isNaN(parseInt(value, 10))) {
			toast.error('Must be a number');

			return false;
		}

		return true;
	};

	private getAms360TagKeyFactOptions(): ISelectOption<ITagKeyFactOption>[] {
		const { integration: i } = this.props;
		const { amsFieldsToUpdate } = this.state;
		const integration = i as Api.IAms360Integration;

		return [
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.branchTagEnabled ?? integration?.tagKeyFactOptions?.branchTagEnabled,
					tagName: 'branchTagEnabled',
				},
				id: `ams360-tag-keyfact-option-branch`,
				text: 'Branch',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.carrierTagEnabled ??
						integration?.tagKeyFactOptions?.carrierTagEnabled,
					tagName: 'carrierTagEnabled',
				},
				id: `ams360-tag-keyfact-option-carrier`,
				text: 'Carrier',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.departmentTagEnabled ??
						integration?.tagKeyFactOptions?.departmentTagEnabled,
					tagName: 'departmentTagEnabled',
				},
				id: `ams360-tag-keyfact-option-department`,
				text: 'Department',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.divisionTagEnabled ??
						integration?.tagKeyFactOptions?.divisionTagEnabled,
					tagName: 'divisionTagEnabled',
				},
				id: `ams360-tag-keyfact-option-division`,
				text: 'Division',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.groupTagEnabled ?? integration?.tagKeyFactOptions?.groupTagEnabled,
					tagName: 'groupTagEnabled',
				},
				id: `ams360-tag-keyfact-option-group`,
				text: 'Group',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.notationTagEnabled ??
						integration?.tagKeyFactOptions?.notationTagEnabled,
					tagName: 'notationTagEnabled',
				},
				id: `ams360-tag-keyfact-option-notation`,
				text: 'Notation',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.stateTagEnabled ?? integration?.tagKeyFactOptions?.stateTagEnabled,
					tagName: 'stateTagEnabled',
				},
				id: `ams360-tag-keyfact-option-state`,
				text: 'State',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.cityTagEnabled ?? integration?.tagKeyFactOptions?.cityTagEnabled,
					tagName: 'cityTagEnabled',
				},
				id: `ams360-tag-keyfact-option-city`,
				text: 'City',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.postalCodeTagEnabled ??
						integration?.tagKeyFactOptions?.postalCodeTagEnabled,
					tagName: 'postalCodeTagEnabled',
				},
				id: `ams360-tag-keyfact-option-postalCode`,
				text: 'Postal Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.countryTagEnabled ??
						integration?.tagKeyFactOptions?.countryTagEnabled,
					tagName: 'countryTagEnabled',
				},
				id: `ams360-country`,
				text: 'Country',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						amsFieldsToUpdate?.tagKeyFactOptions?.healthTagEnabled ?? integration?.tagKeyFactOptions?.healthTagEnabled,
					tagName: 'healthTagEnabled',
				},
				id: `ams360-tag-keyfact-option-health`,
				text: 'Health',
				type: 'checkbox',
			},
		];
	}

	private getHawkSoftTagKeyFactOptions(): ISelectOption<ITagKeyFactOption>[] {
		const { integration: i } = this.props;
		const { hawkSoftFieldsToUpdate } = this.state;
		const integration = i as Api.IHawkSoftIntegration;

		return [
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.billingTypeTagEnabled ??
						integration?.tagKeyFactOptions?.billingTypeTagEnabled,
					tagName: 'billingTypeTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-billing-type`,
				text: 'Billing Type',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.carrierTagEnabled ??
						integration?.tagKeyFactOptions?.carrierTagEnabled,
					tagName: 'carrierTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-carrier`,
				text: 'Carrier',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.relationshipTagEnabled ??
						integration?.tagKeyFactOptions?.relationshipTagEnabled,
					tagName: 'relationshipTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-relationship`,
				text: 'Relationship',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.subStatusTagEnabled ??
						integration?.tagKeyFactOptions?.subStatusTagEnabled,
					tagName: 'subStatusTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-policy-sub-status`,
				text: 'Policy Sub Status',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.sourceTagEnabled ??
						integration?.tagKeyFactOptions?.sourceTagEnabled,
					tagName: 'sourceTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-source`,
				text: 'Source',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.policyTitleTagEnabled ??
						integration?.tagKeyFactOptions?.policyTitleTagEnabled,
					tagName: 'policyTitleTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-title`,
				text: 'Policy Title',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.officeLocationTagEnabled ??
						integration?.tagKeyFactOptions?.officeLocationTagEnabled,
					tagName: 'officeLocationTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-office-location-name`,
				text: 'Office Location Name',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.stateTagEnabled ??
						integration?.tagKeyFactOptions?.stateTagEnabled,
					tagName: 'stateTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-state`,
				text: 'State',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.cityTagEnabled ?? integration?.tagKeyFactOptions?.cityTagEnabled,
					tagName: 'cityTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-city`,
				text: 'City',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.postalCodeTagEnabled ??
						integration?.tagKeyFactOptions?.postalCodeTagEnabled,
					tagName: 'postalCodeTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-postalCode`,
				text: 'Postal Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.countryTagEnabled ??
						integration?.tagKeyFactOptions?.countryTagEnabled,
					tagName: 'countryTagEnabled',
				},
				id: `hawksoft-country`,
				text: 'Country',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.applicationTypeTagEnabled ??
						integration?.tagKeyFactOptions?.applicationTypeTagEnabled,
					tagName: 'applicationTypeTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-application-type`,
				text: 'Application Type',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						hawkSoftFieldsToUpdate?.tagKeyFactOptions?.pendingCancellationTagEnabled ??
						integration?.tagKeyFactOptions?.pendingCancellationTagEnabled,
					tagName: 'pendingCancellationTagEnabled',
				},
				id: `hawksoft-tag-keyfact-option-pending-cancellation`,
				text: 'Pending Cancellation',
				type: 'checkbox',
			},
		];
	}

	private getEclipseTagKeyFactOptions(): ISelectOption<ITagKeyFactOption>[] {
		const { integration: i } = this.props;
		const { eclipseFieldsToUpdate } = this.state;
		const integration = i as Api.IEclipseIntegration;
		const idPrefix = 'eclipse-tag-keyfact';

		return [
			{
				dataContext: {
					enabled:
						eclipseFieldsToUpdate?.tagKeyFactOptions?.carrierTagEnabled ??
						integration?.tagKeyFactOptions?.carrierTagEnabled,
					tagName: 'carrierTagEnabled',
				},
				id: `${idPrefix}-carrier`,
				text: 'Carrier',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						eclipseFieldsToUpdate?.tagKeyFactOptions?.stateTagEnabled ??
						integration?.tagKeyFactOptions?.stateTagEnabled,
					tagName: 'stateTagEnabled',
				},
				id: `${idPrefix}-state`,
				text: 'State',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						eclipseFieldsToUpdate?.tagKeyFactOptions?.cityTagEnabled ?? integration?.tagKeyFactOptions?.cityTagEnabled,
					tagName: 'cityTagEnabled',
				},
				id: `${idPrefix}-city`,
				text: 'City',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						eclipseFieldsToUpdate?.tagKeyFactOptions?.postalCodeTagEnabled ??
						integration?.tagKeyFactOptions?.postalCodeTagEnabled,
					tagName: 'postalCodeTagEnabled',
				},
				id: `${idPrefix}-postalCode`,
				text: 'Postal Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						eclipseFieldsToUpdate?.tagKeyFactOptions?.countryTagEnabled ??
						integration?.tagKeyFactOptions?.countryTagEnabled,
					tagName: 'countryTagEnabled',
				},
				id: `eclipse-country`,
				text: 'Country',
				type: 'checkbox',
			},
		];
	}

	private getEzlynxTagKeyFactOptions(): ISelectOption<ITagKeyFactOption>[] {
		const { integration: i } = this.props;
		const { ezLynxFieldsToUpdate } = this.state;
		const integration = i as Api.IEzLynxCsvIntegration;
		const idPrefix = 'ezlynx-tag-keyfact';

		return [
			{
				dataContext: {
					enabled:
						ezLynxFieldsToUpdate?.tagKeyFactOptions?.agencyCodeTagEnabled ??
						integration?.tagKeyFactOptions?.agencyCodeTagEnabled,
					tagName: 'agencyCodeTagEnabled',
				},
				id: `${idPrefix}-agencyCode`,
				text: 'Agency Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						ezLynxFieldsToUpdate?.tagKeyFactOptions?.carrierTagEnabled ??
						integration?.tagKeyFactOptions?.carrierTagEnabled,
					tagName: 'carrierTagEnabled',
				},
				id: `${idPrefix}-carrier`,
				text: 'Carrier',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						ezLynxFieldsToUpdate?.tagKeyFactOptions?.sourceTagEnabled ??
						integration?.tagKeyFactOptions?.sourceTagEnabled,
					tagName: 'sourceTagEnabled',
				},
				id: `${idPrefix}-source`,
				text: 'Source',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						ezLynxFieldsToUpdate?.tagKeyFactOptions?.stateTagEnabled ?? integration?.tagKeyFactOptions?.stateTagEnabled,
					tagName: 'stateTagEnabled',
				},
				id: `${idPrefix}-state`,
				text: 'State',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						ezLynxFieldsToUpdate?.tagKeyFactOptions?.cityTagEnabled ?? integration?.tagKeyFactOptions?.cityTagEnabled,
					tagName: 'cityTagEnabled',
				},
				id: `${idPrefix}-city`,
				text: 'City',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						ezLynxFieldsToUpdate?.tagKeyFactOptions?.postalCodeTagEnabled ??
						integration?.tagKeyFactOptions?.postalCodeTagEnabled,
					tagName: 'postalCodeTagEnabled',
				},
				id: `${idPrefix}-postalCode`,
				text: 'Postal Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						ezLynxFieldsToUpdate?.tagKeyFactOptions?.countryTagEnabled ??
						integration?.tagKeyFactOptions?.countryTagEnabled,
					tagName: 'countryTagEnabled',
				},
				id: `ezlynx-country`,
				text: 'Country',
				type: 'checkbox',
			},
		];
	}

	private getEpicTagKeyFactOptions(): ISelectOption<ITagKeyFactOption>[] {
		const { integration: i } = this.props;
		const { epicFieldsToUpdate } = this.state;
		const integration = i as Api.IEpicCsvIntegration;
		const idPrefix = 'epic-tag-keyfact';

		return [
			{
				dataContext: {
					enabled:
						epicFieldsToUpdate?.tagKeyFactOptions?.carrierTagEnabled ??
						integration?.tagKeyFactOptions?.carrierTagEnabled,
					tagName: 'carrierTagEnabled',
				},
				id: `${idPrefix}-carrier`,
				text: 'Carrier',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						epicFieldsToUpdate?.tagKeyFactOptions?.agencyCodeTagEnabled ??
						integration?.tagKeyFactOptions?.agencyCodeTagEnabled,
					tagName: 'agencyCodeTagEnabled',
				},
				id: `${idPrefix}-agencyCode`,
				text: 'Agency Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						epicFieldsToUpdate?.tagKeyFactOptions?.stateTagEnabled ?? integration?.tagKeyFactOptions?.stateTagEnabled,
					tagName: 'stateTagEnabled',
				},
				id: `${idPrefix}-state`,
				text: 'State',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						epicFieldsToUpdate?.tagKeyFactOptions?.cityTagEnabled ?? integration?.tagKeyFactOptions?.cityTagEnabled,
					tagName: 'cityTagEnabled',
				},
				id: `${idPrefix}-city`,
				text: 'City',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						epicFieldsToUpdate?.tagKeyFactOptions?.postalCodeTagEnabled ??
						integration?.tagKeyFactOptions?.postalCodeTagEnabled,
					tagName: 'postalCodeTagEnabled',
				},
				id: `${idPrefix}-postalCode`,
				text: 'Postal Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						epicFieldsToUpdate?.tagKeyFactOptions?.countryTagEnabled ??
						integration?.tagKeyFactOptions?.countryTagEnabled,
					tagName: 'countryTagEnabled',
				},
				id: `epic-country`,
				text: 'Country',
				type: 'checkbox',
			},
		];
	}

	private getQQTagKeyFactOptions(): ISelectOption<ITagKeyFactOption>[] {
		const { integration: i } = this.props;
		const { qqFieldsToUpdate } = this.state;
		const integration = i as Api.IQQIntegration;
		const idPrefix = 'qq-tag-keyfact';

		return [
			{
				dataContext: {
					enabled:
						qqFieldsToUpdate?.tagKeyFactOptions?.carrierTagEnabled ?? integration?.tagKeyFactOptions?.carrierTagEnabled,
					tagName: 'carrierTagEnabled',
				},
				id: `${idPrefix}-carrier`,
				text: 'Carrier',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						qqFieldsToUpdate?.tagKeyFactOptions?.policySourceTagEnabled ??
						integration?.tagKeyFactOptions?.policySourceTagEnabled,
					tagName: 'policySourceTagEnabled',
				},
				id: `${idPrefix}-policy-source`,
				text: 'Policy Source',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						qqFieldsToUpdate?.tagKeyFactOptions?.locationTagEnabled ??
						integration?.tagKeyFactOptions?.locationTagEnabled,
					tagName: 'locationTagEnabled',
				},
				id: `${idPrefix}-location`,
				text: 'Location',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						qqFieldsToUpdate?.tagKeyFactOptions?.stateTagEnabled ?? integration?.tagKeyFactOptions?.stateTagEnabled,
					tagName: 'stateTagEnabled',
				},
				id: `${idPrefix}-state`,
				text: 'State',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						qqFieldsToUpdate?.tagKeyFactOptions?.cityTagEnabled ?? integration?.tagKeyFactOptions?.cityTagEnabled,
					tagName: 'cityTagEnabled',
				},
				id: `${idPrefix}-city`,
				text: 'City',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						qqFieldsToUpdate?.tagKeyFactOptions?.postalCodeTagEnabled ??
						integration?.tagKeyFactOptions?.postalCodeTagEnabled,
					tagName: 'postalCodeTagEnabled',
				},
				id: `${idPrefix}-postalCode`,
				text: 'Postal Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						qqFieldsToUpdate?.tagKeyFactOptions?.countryTagEnabled ?? integration?.tagKeyFactOptions?.countryTagEnabled,
					tagName: 'countryTagEnabled',
				},
				id: `qq-country`,
				text: 'Country',
				type: 'checkbox',
			},
		];
	}

	private getXanatekTagKeyFactOptions(): ISelectOption<ITagKeyFactOption>[] {
		const { integration: i } = this.props;
		const { xanatekFieldsToUpdate } = this.state;
		const integration = i as Api.IGeneralCsvIntegration;
		const idPrefix = 'xanatek-tag-keyfact';

		return [
			{
				dataContext: {
					enabled:
						xanatekFieldsToUpdate?.tagKeyFactOptions?.carrierTagEnabled ??
						integration?.tagKeyFactOptions?.carrierTagEnabled,
					tagName: 'carrierTagEnabled',
				},
				id: `${idPrefix}-carrier`,
				text: 'Carrier',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						xanatekFieldsToUpdate?.tagKeyFactOptions?.stateTagEnabled ??
						integration?.tagKeyFactOptions?.stateTagEnabled,
					tagName: 'stateTagEnabled',
				},
				id: `${idPrefix}-state`,
				text: 'State',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						xanatekFieldsToUpdate?.tagKeyFactOptions?.cityTagEnabled ?? integration?.tagKeyFactOptions?.cityTagEnabled,
					tagName: 'cityTagEnabled',
				},
				id: `${idPrefix}-city`,
				text: 'City',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						xanatekFieldsToUpdate?.tagKeyFactOptions?.postalCodeTagEnabled ??
						integration?.tagKeyFactOptions?.postalCodeTagEnabled,
					tagName: 'postalCodeTagEnabled',
				},
				id: `${idPrefix}-postalCode`,
				text: 'Postal Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						xanatekFieldsToUpdate?.tagKeyFactOptions?.countryTagEnabled ??
						integration?.tagKeyFactOptions?.countryTagEnabled,
					tagName: 'countryTagEnabled',
				},
				id: `xanatek-country`,
				text: 'Country',
				type: 'checkbox',
			},
		];
	}

	private getRedtailTagKeyFactOptions(): ISelectOption<ITagKeyFactOption>[] {
		const { integration: i } = this.props;
		const { redtailFieldsToUpdate } = this.state;
		const integration = i as Api.IRedtailIntegration;
		const idPrefix = 'redtail-tag-keyfact';

		return [
			{
				dataContext: {
					enabled:
						redtailFieldsToUpdate?.tagKeyFactOptions?.sourceTagEnabled ??
						integration?.tagKeyFactOptions?.sourceTagEnabled,
					tagName: 'sourceTagEnabled',
				},
				id: `${idPrefix}-source`,
				text: 'Source',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						redtailFieldsToUpdate?.tagKeyFactOptions?.categoryTagEnabled ??
						integration?.tagKeyFactOptions?.categoryTagEnabled,
					tagName: 'categoryTagEnabled',
				},
				id: `${idPrefix}-category`,
				text: 'Category',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						redtailFieldsToUpdate?.tagKeyFactOptions?.keywordTagsEnabled ??
						integration?.tagKeyFactOptions?.keywordTagsEnabled,
					tagName: 'keywordTagsEnabled',
				},
				id: `${idPrefix}-keywords`,
				text: 'Keywords',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						redtailFieldsToUpdate?.tagKeyFactOptions?.stateTagEnabled ??
						integration?.tagKeyFactOptions?.stateTagEnabled,
					tagName: 'stateTagEnabled',
				},
				id: `${idPrefix}-state`,
				text: 'State',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						redtailFieldsToUpdate?.tagKeyFactOptions?.cityTagEnabled ?? integration?.tagKeyFactOptions?.cityTagEnabled,
					tagName: 'cityTagEnabled',
				},
				id: `${idPrefix}-city`,
				text: 'City',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						redtailFieldsToUpdate?.tagKeyFactOptions?.postalCodeTagEnabled ??
						integration?.tagKeyFactOptions?.postalCodeTagEnabled,
					tagName: 'postalCodeTagEnabled',
				},
				id: `${idPrefix}-postalCode`,
				text: 'Postal Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						redtailFieldsToUpdate?.tagKeyFactOptions?.countryTagEnabled ??
						integration?.tagKeyFactOptions?.countryTagEnabled,
					tagName: 'countryTagEnabled',
				},
				id: `redtail-country`,
				text: 'Country',
				type: 'checkbox',
			},
		];
	}

	private getClioTagKeyFactOptions(): ISelectOption<ITagKeyFactOption>[] {
		const { integration: i } = this.props;
		const { clioFieldsToUpdate } = this.state;
		const integration = i as Api.IClioIntegration;
		const idPrefix = 'clio-tag-keyfact';
		return [
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.groupPracticeAreaTagEnabled ??
						integration?.tagKeyFactOptions?.groupPracticeAreaTagEnabled,
					tagName: 'groupPracticeAreaTagEnabled',
				},
				id: `${idPrefix}-group-practice-area`,
				text: 'Practice Area',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.groupPracticeAreaTagForRelatedEnabled ??
						integration?.tagKeyFactOptions?.groupPracticeAreaTagForRelatedEnabled,
					tagName: 'groupPracticeAreaTagForRelatedEnabled',
				},
				id: `${idPrefix}-group-practice-area-related`,
				text: 'Practice Area (Related)',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.customFieldCheckboxTagEnabled ??
						integration?.tagKeyFactOptions?.customFieldCheckboxTagEnabled,
					tagName: 'customFieldCheckboxTagEnabled',
				},
				id: `${idPrefix}-custom-field-checkbox`,
				text: 'Custom Checkbox',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.customFieldPicklistTagEnabled ??
						integration?.tagKeyFactOptions?.customFieldPicklistTagEnabled,
					tagName: 'customFieldPicklistTagEnabled',
				},
				id: `${idPrefix}-custom-field-picklist`,
				text: 'Custom Picklist',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.relationshipTagEnabled ??
						integration?.tagKeyFactOptions?.relationshipTagEnabled,
					tagName: 'relationshipTagEnabled',
				},
				id: `${idPrefix}-relationship`,
				text: 'Relationships',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.matterNumberTagEnabled ??
						integration?.tagKeyFactOptions?.matterNumberTagEnabled ??
						true,
					tagName: 'matterNumberTagEnabled',
				},
				id: `${idPrefix}-matter-number`,
				text: 'Matter Number',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.lastClosedMatterYearTagEnabled ??
						integration?.tagKeyFactOptions?.lastClosedMatterYearTagEnabled,
					tagName: 'lastClosedMatterYearTagEnabled',
				},
				id: `${idPrefix}-lastClosedMatterYear`,
				text: 'Last Closed Matter Year',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.stateTagEnabled ?? integration?.tagKeyFactOptions?.stateTagEnabled,
					tagName: 'stateTagEnabled',
				},
				id: `${idPrefix}-state`,
				text: 'State',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.cityTagEnabled ?? integration?.tagKeyFactOptions?.cityTagEnabled,
					tagName: 'cityTagEnabled',
				},
				id: `${idPrefix}-city`,
				text: 'City',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.postalCodeTagEnabled ??
						integration?.tagKeyFactOptions?.postalCodeTagEnabled,
					tagName: 'postalCodeTagEnabled',
				},
				id: `${idPrefix}-postalCode`,
				text: 'Postal Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						clioFieldsToUpdate?.tagKeyFactOptions?.countryTagEnabled ??
						integration?.tagKeyFactOptions?.countryTagEnabled,
					tagName: 'countryTagEnabled',
				},
				id: `clio-country`,
				text: 'Country',
				type: 'checkbox',
			},
		];
	}

	private getDonorPerfectTagKeyFactOptions(): ISelectOption<ITagKeyFactOption>[] {
		const { integration: i } = this.props;
		const { donorPerfectFieldsToUpdate } = this.state;
		const integration = i as Api.IDonorPerfectIntegration;
		const idPrefix = 'donorPerfect-tag-keyfact';
		return [
			{
				dataContext: {
					enabled:
						donorPerfectFieldsToUpdate?.tagKeyFactOptions?.donorTypeTagEnabled ??
						integration?.tagKeyFactOptions?.donorTypeTagEnabled,
					tagName: 'donorTypeTagEnabled',
				},
				id: `${idPrefix}-donor-type`,
				text: 'Donor Type',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						donorPerfectFieldsToUpdate?.tagKeyFactOptions?.majorDonorTagEnabled ??
						integration?.tagKeyFactOptions?.majorDonorTagEnabled,
					tagName: 'majorDonorTagEnabled',
				},
				id: `${idPrefix}-major-donor`,
				text: 'Major Donor',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						donorPerfectFieldsToUpdate?.tagKeyFactOptions?.stateTagEnabled ??
						integration?.tagKeyFactOptions?.stateTagEnabled,
					tagName: 'stateTagEnabled',
				},
				id: `${idPrefix}-state`,
				text: 'State',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						donorPerfectFieldsToUpdate?.tagKeyFactOptions?.cityTagEnabled ??
						integration?.tagKeyFactOptions?.cityTagEnabled,
					tagName: 'cityTagEnabled',
				},
				id: `${idPrefix}-city`,
				text: 'City',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						donorPerfectFieldsToUpdate?.tagKeyFactOptions?.postalCodeTagEnabled ??
						integration?.tagKeyFactOptions?.postalCodeTagEnabled,
					tagName: 'postalCodeTagEnabled',
				},
				id: `${idPrefix}-postalCode`,
				text: 'Postal Code',
				type: 'checkbox',
			},
			{
				dataContext: {
					enabled:
						donorPerfectFieldsToUpdate?.tagKeyFactOptions?.countryTagEnabled ??
						integration?.tagKeyFactOptions?.countryTagEnabled,
					tagName: 'countryTagEnabled',
				},
				id: `donorPerfect-country`,
				text: 'Country',
				type: 'checkbox',
			},
		];
	}

	private getSelectedProspectOption() {
		const { sharedFieldsToUpdate } = this.state;
		const { integration } = this.props;
		return (
			ProspectOptionsOptions.find(x => {
				return (
					x.dataContext ===
					(sharedFieldsToUpdate?.prospectOptions?.prospectIntegration ??
						integration?.prospectOptions?.prospectIntegration)
				);
			}) ?? ProspectOptionsOptions[1]
		);
	}

	private renderProspectOptions() {
		const { integration } = this.props;
		if (integration?.prospectOptions) {
			switch (integration.prospectOptions?.prospectIntegration) {
				case Api.ProspectIntegration.All:
					return 'All';
				case Api.ProspectIntegration.None:
					return 'None';
				case Api.ProspectIntegration.Timeline:
					return `Last ${integration?.prospectOptions?.timelineYears} years`;
				default:
					return 'None';
			}
		} else {
			return 'None';
		}
	}

	private onEditClick = () => {
		this.setState({ isEditing: true });
	};

	private onAmsMappingChange = (option: ISelectOption<Api.Ams360IntegrationOwnershipRole>) => {
		const { amsFieldsToUpdate } = this.state;
		this.setState({
			amsFieldsToUpdate: {
				...amsFieldsToUpdate,
				ownershipRole: option.dataContext,
			},
		});
	};

	private onHawkSoftMappingChange = (option: ISelectOption<Api.BaseInsuranceIntegrationOwnershipRole>) => {
		const { hawkSoftFieldsToUpdate } = this.state;
		this.setState({
			hawkSoftFieldsToUpdate: {
				...hawkSoftFieldsToUpdate,
				ownershipRole: option.dataContext,
			},
		});
	};

	private onEclipseMappingChange = (option: ISelectOption<Api.BaseInsuranceIntegrationOwnershipRole>) => {
		const { eclipseFieldsToUpdate } = this.state;
		this.setState({
			eclipseFieldsToUpdate: {
				...eclipseFieldsToUpdate,
				ownershipRole: option.dataContext,
			},
		});
	};

	private onXanatekMappingChange = (option: ISelectOption<Api.BaseInsuranceIntegrationOwnershipRole>) => {
		const { xanatekFieldsToUpdate } = this.state;
		this.setState({
			xanatekFieldsToUpdate: {
				...xanatekFieldsToUpdate,
				ownershipRole: option.dataContext,
			},
		});
	};

	private onEpicMappingChange = (option: ISelectOption<Api.BaseInsuranceIntegrationOwnershipRole>) => {
		const { epicFieldsToUpdate } = this.state;
		this.setState({
			epicFieldsToUpdate: {
				...epicFieldsToUpdate,
				ownershipRole: option.dataContext,
			},
		});
	};

	private onQQMappingChange = (option: ISelectOption<Api.BaseInsuranceIntegrationOwnershipRole>) => {
		const { qqFieldsToUpdate } = this.state;
		this.setState({
			qqFieldsToUpdate: {
				...qqFieldsToUpdate,
				ownershipRole: option.dataContext,
			},
		});
	};

	private onRedtailMappingChange = (option: ISelectOption<Api.RedtailIntegrationOwnershipRole>) => {
		const { redtailFieldsToUpdate } = this.state;
		this.setState({
			redtailFieldsToUpdate: {
				...redtailFieldsToUpdate,
				ownershipRole: option.dataContext,
			},
		});
	};

	private onDonorPerfectMappingChange = (option: ISelectOption<Api.DonorPerfectIntegrationOwnershipRole>) => {
		const { donorPerfectFieldsToUpdate } = this.state;
		this.setState({
			donorPerfectFieldsToUpdate: {
				...donorPerfectFieldsToUpdate,
				ownershipRole: option.dataContext,
			},
		});
	};

	private onImportInactiveClientsChange =
		(integrationType: Api.ConfigurableIntegrationType) => (option: ISelectOption<boolean>) => {
			const { amsFieldsToUpdate } = this.state;

			if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
				this.setState({
					amsFieldsToUpdate: {
						...amsFieldsToUpdate,
						inactiveClientsEnabled: option.dataContext,
					},
				});
			}
		};

	private onNonClientOptionChange = (option: ISelectOption<boolean>) => {
		const { integrationType } = this.props;
		const { clioFieldsToUpdate } = this.state;

		if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			this.setState({
				clioFieldsToUpdate: {
					...clioFieldsToUpdate,
					nonClients: option.dataContext,
				},
			});
		}
	};

	private onRelatedContactsOptionChange = (option: ISelectOption<boolean>) => {
		const { integrationType } = this.props;
		const { clioFieldsToUpdate } = this.state;

		if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			this.setState({
				clioFieldsToUpdate: {
					...clioFieldsToUpdate,
					relatedContacts: option.dataContext,
				},
			});
		}
	};

	private onClioOwnershipRoleChange = (option: ISelectOption<Api.ClioIntegrationOwnershipRole>) => {
		this.setState({
			clioFieldsToUpdate: {
				...this.state.clioFieldsToUpdate,
				ownershipRole: option.dataContext,
			},
		});
	};

	private onClioAttorneyTagSettingsChange =
		(attorneyType: Api.ClioIntegrationOwnershipRole) => (option: ISelectOption<Api.AttorneyTagOption>) => {
			const nextState: IState = this.state;
			const { integration: i } = this.props;
			const integration = i ? (i as Api.IClioIntegration) : null;
			const responsibleAttorneyTagOption =
				this.state.clioFieldsToUpdate?.tagKeyFactOptions?.responsibleAttorneyTagOption ??
				integration.tagKeyFactOptions?.responsibleAttorneyTagOption;
			const originatingAttorneyTagOption =
				this.state.clioFieldsToUpdate?.tagKeyFactOptions?.originatingAttorneyTagOption ??
				integration.tagKeyFactOptions?.originatingAttorneyTagOption;

			nextState.clioFieldsToUpdate = {
				...this.state.clioFieldsToUpdate,
				tagKeyFactOptions: {
					...this.state.clioFieldsToUpdate.tagKeyFactOptions,
					originatingAttorneyTagOption:
						attorneyType === Api.ClioIntegrationOwnershipRole.OriginatingAttorney
							? option.dataContext
							: originatingAttorneyTagOption,
					responsibleAttorneyTagOption:
						attorneyType === Api.ClioIntegrationOwnershipRole.ResponsibleAttorney
							? option.dataContext
							: responsibleAttorneyTagOption,
				},
			};
			this.setState(nextState);
		};

	private onOwnershipRoleChange =
		(integrationType: Api.ConfigurableIntegrationType) => (option: ISelectOption<boolean>) => {
			const {
				amsFieldsToUpdate,
				hawkSoftFieldsToUpdate,
				eclipseFieldsToUpdate,
				xanatekFieldsToUpdate,
				epicFieldsToUpdate,
				qqFieldsToUpdate,
			} = this.state;

			const nextState: IState = this.state;

			if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
				nextState.amsFieldsToUpdate = {
					...amsFieldsToUpdate,
					tagKeyFactOptions: {
						...amsFieldsToUpdate.tagKeyFactOptions,
						displayBothOwnershipRoleTags: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
				nextState.hawkSoftFieldsToUpdate = {
					...hawkSoftFieldsToUpdate,
					tagKeyFactOptions: {
						...hawkSoftFieldsToUpdate.tagKeyFactOptions,
						displayBothOwnershipRoleTags: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
				nextState.eclipseFieldsToUpdate = {
					...eclipseFieldsToUpdate,
					tagKeyFactOptions: {
						...eclipseFieldsToUpdate.tagKeyFactOptions,
						displayBothOwnershipRoleTags: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
				nextState.xanatekFieldsToUpdate = {
					...xanatekFieldsToUpdate,
					tagKeyFactOptions: {
						...xanatekFieldsToUpdate.tagKeyFactOptions,
						displayBothOwnershipRoleTags: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
				nextState.epicFieldsToUpdate = {
					...epicFieldsToUpdate,
					tagKeyFactOptions: {
						...epicFieldsToUpdate.tagKeyFactOptions,
						displayBothOwnershipRoleTags: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
				nextState.qqFieldsToUpdate = {
					...qqFieldsToUpdate,
					tagKeyFactOptions: {
						...qqFieldsToUpdate.tagKeyFactOptions,
						displayBothOwnershipRoleTags: option.dataContext,
					},
				};
			}

			this.setState(nextState);
		};

	private onProspectOptionsChanged = (option: ISelectOption<Api.ProspectIntegration>) => {
		const { sharedFieldsToUpdate } = this.state;
		if (option.dataContext === Api.ProspectIntegration.Timeline) {
			this.setState({ showNumberOfYearsModal: true });
			return;
		}

		this.setState({
			sharedFieldsToUpdate: {
				...sharedFieldsToUpdate,
				prospectOptions: {
					prospectIntegration: option.dataContext,
				},
			},
		});
	};

	private onHawkSoftSubstatusOptionsClose = (selectedOptions: ISelectOption<string>[]) => {
		const integration = this.props.integration as Api.IHawkSoftIntegration;

		const tagKeyFactOptions = integration.tagKeyFactOptions;
		if (!tagKeyFactOptions.subStatusTagOption) {
			return;
		}

		const selectedSubStatusOptions = selectedOptions.map(e => e.dataContext as Api.SubStatusIntegration);

		const nextState = produce(this.state, draft => {
			draft.hawkSoftFieldsToUpdate = draft.hawkSoftFieldsToUpdate || {};
			draft.hawkSoftFieldsToUpdate.tagKeyFactOptions = draft.hawkSoftFieldsToUpdate.tagKeyFactOptions || {};
			draft.hawkSoftFieldsToUpdate.tagKeyFactOptions.subStatusTagOption = {
				...(draft.hawkSoftFieldsToUpdate.tagKeyFactOptions.subStatusTagOption || {}),
				subStatusOptions: selectedSubStatusOptions,
			};

			if (selectedOptions.find(s => s.dataContext === Api.SubStatusIntegration.Cancelled.toString())) {
				draft.showSubStatusNumberOfYearsModal = true;
			}
		});

		this.setState(nextState);
	};

	private onBirthdayKeyFactChange =
		(integrationType: Api.ConfigurableIntegrationType) => (option: ISelectOption<Api.BirthdayKeyFactDisplay>) => {
			const {
				amsFieldsToUpdate,
				hawkSoftFieldsToUpdate,
				eclipseFieldsToUpdate,
				redtailFieldsToUpdate,
				clioFieldsToUpdate,
				ezLynxFieldsToUpdate,
				epicFieldsToUpdate,
				qqFieldsToUpdate,
				xanatekFieldsToUpdate,
			} = this.state;

			const nextState: IState = this.state;

			if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
				nextState.amsFieldsToUpdate = {
					...amsFieldsToUpdate,
					tagKeyFactOptions: {
						...amsFieldsToUpdate.tagKeyFactOptions,
						birthdayKeyFactDisplay: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
				nextState.hawkSoftFieldsToUpdate = {
					...hawkSoftFieldsToUpdate,
					tagKeyFactOptions: {
						...hawkSoftFieldsToUpdate.tagKeyFactOptions,
						birthdayKeyFactDisplay: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
				nextState.eclipseFieldsToUpdate = {
					...eclipseFieldsToUpdate,
					tagKeyFactOptions: {
						...eclipseFieldsToUpdate.tagKeyFactOptions,
						birthdayKeyFactDisplay: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
				nextState.redtailFieldsToUpdate = {
					...redtailFieldsToUpdate,
					tagKeyFactOptions: {
						...redtailFieldsToUpdate.tagKeyFactOptions,
						birthdayKeyFactDisplay: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
				nextState.clioFieldsToUpdate = {
					...clioFieldsToUpdate,
					tagKeyFactOptions: {
						...clioFieldsToUpdate.tagKeyFactOptions,
						birthdayKeyFactDisplay: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
				nextState.ezLynxFieldsToUpdate = {
					...ezLynxFieldsToUpdate,
					tagKeyFactOptions: {
						...ezLynxFieldsToUpdate.tagKeyFactOptions,
						birthdayKeyFactDisplay: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
				nextState.epicFieldsToUpdate = {
					...epicFieldsToUpdate,
					tagKeyFactOptions: {
						...epicFieldsToUpdate.tagKeyFactOptions,
						birthdayKeyFactDisplay: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
				nextState.qqFieldsToUpdate = {
					...qqFieldsToUpdate,
					tagKeyFactOptions: {
						...qqFieldsToUpdate.tagKeyFactOptions,
						birthdayKeyFactDisplay: option.dataContext,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
				nextState.xanatekFieldsToUpdate = {
					...xanatekFieldsToUpdate,
					tagKeyFactOptions: {
						...xanatekFieldsToUpdate.tagKeyFactOptions,
						birthdayKeyFactDisplay: option.dataContext,
					},
				};
			}

			this.setState(nextState);
		};

	private onSalutationAsFirstNameChange =
		(integrationType: Api.ConfigurableIntegrationType) => (option: ISelectOption<boolean>) => {
			const { eclipseFieldsToUpdate } = this.state;

			const nextState: IState = this.state;

			if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
				nextState.eclipseFieldsToUpdate = {
					...eclipseFieldsToUpdate,
					useSalutationAsFirstName: option.dataContext,
				};
			}
			this.setState(nextState);
		};

	private onPrimaryEmailOnlyChange =
		(integrationType: Api.ConfigurableIntegrationType) => (option: ISelectOption<boolean>) => {
			const {
				amsFieldsToUpdate,
				hawkSoftFieldsToUpdate,
				eclipseFieldsToUpdate,
				redtailFieldsToUpdate,
				clioFieldsToUpdate,
				ezLynxFieldsToUpdate,
				epicFieldsToUpdate,
				qqFieldsToUpdate,
				hubSpotFieldsToUpdate,
				xanatekFieldsToUpdate,
				donorPerfectFieldsToUpdate,
			} = this.state;

			const nextState: IState = this.state;
			if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
				nextState.amsFieldsToUpdate = {
					...amsFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
				nextState.hawkSoftFieldsToUpdate = {
					...hawkSoftFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
				nextState.eclipseFieldsToUpdate = {
					...eclipseFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
				nextState.redtailFieldsToUpdate = {
					...redtailFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
				nextState.clioFieldsToUpdate = {
					...clioFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
				nextState.ezLynxFieldsToUpdate = {
					...ezLynxFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
				nextState.epicFieldsToUpdate = {
					...epicFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
				nextState.qqFieldsToUpdate = {
					...qqFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.HubSpot) {
				nextState.hubSpotFieldsToUpdate = {
					...hubSpotFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
				nextState.xanatekFieldsToUpdate = {
					...xanatekFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.DonorPerfect) {
				nextState.donorPerfectFieldsToUpdate = {
					...donorPerfectFieldsToUpdate,
					primaryEmailOnly: option.dataContext,
				};
			}
			this.setState(nextState);
		};

	private onUseForMeetingSchedulingChanged =
		(integrationType: Api.ConfigurableIntegrationType) => (option: ISelectOption<boolean>) => {
			const { redtailFieldsToUpdate } = this.state;

			const nextState: IState = this.state;

			if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
				nextState.redtailFieldsToUpdate = {
					...redtailFieldsToUpdate,
					useForMeetingScheduling: option.dataContext,
				};
			}
			this.setState(nextState);
		};

	private onSaveClick = () => {
		const { onUpdateIntegrationSettings, integrationType } = this.props;
		let fieldsToUpdate = { ...this.state.sharedFieldsToUpdate };

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			fieldsToUpdate = {
				...fieldsToUpdate,
				...this.state.amsFieldsToUpdate,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			fieldsToUpdate = {
				...fieldsToUpdate,
				...this.state.hawkSoftFieldsToUpdate,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			fieldsToUpdate = {
				...fieldsToUpdate,
				...this.state.eclipseFieldsToUpdate,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			fieldsToUpdate = {
				...fieldsToUpdate,
				...this.state.redtailFieldsToUpdate,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			fieldsToUpdate = {
				...fieldsToUpdate,
				...this.state.clioFieldsToUpdate,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
			fieldsToUpdate = {
				...fieldsToUpdate,
				...this.state.ezLynxFieldsToUpdate,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
			fieldsToUpdate = {
				...fieldsToUpdate,
				...this.state.epicFieldsToUpdate,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			fieldsToUpdate = {
				...fieldsToUpdate,
				...this.state.qqFieldsToUpdate,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
			fieldsToUpdate = {
				...fieldsToUpdate,
				...this.state.xanatekFieldsToUpdate,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.DonorPerfect) {
			fieldsToUpdate = {
				...fieldsToUpdate,
				...this.state.donorPerfectFieldsToUpdate,
			};
		}
		onUpdateIntegrationSettings(fieldsToUpdate);
		this.clearFieldsToUpdate();
	};

	private clearFieldsToUpdate = () => {
		this.setState({
			amsFieldsToUpdate: {},
			clioFieldsToUpdate: {},
			eclipseFieldsToUpdate: {},
			epicFieldsToUpdate: {},
			ezLynxFieldsToUpdate: {},
			hawkSoftFieldsToUpdate: {},
			isEditing: false,
			qqFieldsToUpdate: {},
			redtailFieldsToUpdate: {},
			xanatekFieldsToUpdate: {},
			donorPerfectFieldsToUpdate: {},
		});
	};

	private getOwnershipRole() {
		const { integration, integrationType } = this.props;
		switch (integrationType) {
			case Api.ConfigurableIntegrationType.Ams360: {
				const ams = integration as Api.IAms360Integration;
				return ams.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.HawkSoft: {
				const hawksoft = integration as Api.IHawkSoftIntegration;
				return hawksoft.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.Eclipse: {
				const eclipse = integration as Api.IEclipseIntegration;
				return eclipse.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.Epic: {
				const epic = integration as Api.IEpicCsvIntegration;
				return epic.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.Redtail: {
				const option =
					(integration as Api.IRedtailIntegration).ownershipRole?.toString() ||
					Api.RedtailIntegrationOwnershipRole.FirstObservedUserFromContact;
				return RedtailOwnershipRoleOptions.find(x => x.dataContext === option)?.text || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.QQ: {
				const qq = integration as Api.IQQIntegration;
				return qq.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.Xanatek: {
				const xanatek = integration as Api.IGeneralCsvIntegration;
				return xanatek.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.DonorPerfect: {
				const donorPerfect = integration as Api.IDonorPerfectIntegration;
				return donorPerfect.ownershipRole?.toString() || 'Unknown';
			}
			default: {
				return null;
			}
		}
	}

	private onChangeAgencyId = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({
			agencyId: event.target.value,
		});
	};

	private onOwnershipRoleChanged = (ownershipRole: string) => {
		const { context } = this.state;
		this.setState({
			context: {
				...((context || {}) as IConfigurableIntegrationSettingsContext),
				ownershipRole,
			},
		});
	};

	private onSignIn = () => {
		const { integrationType, onSignIn } = this.props;
		const { agencyId, context } = this.state;
		if (onSignIn) {
			switch (integrationType) {
				case Api.ConfigurableIntegrationType.HawkSoft: {
					const hawksoftIntegration: Api.IHawkSoftIntegration = {
						agencyId,
						ownershipRole: context?.ownershipRole as Api.BaseInsuranceIntegrationOwnershipRole,
					};
					onSignIn(hawksoftIntegration);
					break;
				}
				case Api.ConfigurableIntegrationType.Eclipse: {
					const eclipseIntegration: Api.IEclipseIntegration = {
						agencyId,
						ownershipRole: context?.ownershipRole as Api.BaseInsuranceIntegrationOwnershipRole,
					};
					onSignIn(eclipseIntegration);
					break;
				}
				default: {
					const amsIntegration: Api.IAms360Integration = {
						agencyId,
						ownershipRole: context?.ownershipRole as Api.Ams360IntegrationOwnershipRole,
					};
					onSignIn(amsIntegration);
					break;
				}
			}
		}
	};

	private onShowUserCredentialsModal = () => {
		this.setState({ isUserCredentialsModalOpened: true });
	};

	private onCloseUserCredentialsModal = () => {
		this.setState({ isUserCredentialsModalOpened: false });
	};

	private onRedtailAvailableContactTypesChange = (selectedOptions: ISelectOption<string>[]) => {
		const integration = this.props.integration as Api.IRedtailIntegration;
		const { redtailFieldsToUpdate } = this.state;
		const availableContactTypes = selectedOptions.map(opt => opt.dataContext);
		const blockedContactTypes = integration.availableContactTypes.filter(opt => !availableContactTypes.includes(opt));

		this.setState({
			redtailFieldsToUpdate: {
				...redtailFieldsToUpdate,
				availableContactTypes,
				blockedContactTypes,
			},
		});
	};

	private onRedtailAvailableCategoriesChange = (selectedOptions: ISelectOption<string>[]) => {
		const integration = this.props.integration as Api.IRedtailIntegration;
		const { redtailFieldsToUpdate } = this.state;
		const availableCategories = selectedOptions.map(opt => opt.dataContext);
		const blockedCategories = integration.availableCategories.filter(opt => !availableCategories.includes(opt));

		this.setState({
			redtailFieldsToUpdate: {
				...redtailFieldsToUpdate,
				availableCategories,
				blockedCategories,
			},
		});
	};

	private onRedtailAvailableStatusesChange = (selectedOptions: ISelectOption<string>[]) => {
		const integration = this.props.integration as Api.IRedtailIntegration;
		const { redtailFieldsToUpdate } = this.state;
		const selected = selectedOptions.map(opt => opt.dataContext);
		const blockedStatuses = integration.availableStatuses.filter(opt => !selected.includes(opt));

		this.setState({
			redtailFieldsToUpdate: {
				...redtailFieldsToUpdate,
				blockedStatuses,
			},
		});
	};

	private onCommercialProfileChange = ({ dataContext }: ISelectOption<Api.CommercialProfiles>) => {
		const { integrationType } = this.props;
		const { amsFieldsToUpdate, eclipseFieldsToUpdate, hawkSoftFieldsToUpdate, qqFieldsToUpdate } = this.state;
		const nextState = { ...this.state };

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			nextState.amsFieldsToUpdate = {
				...amsFieldsToUpdate,
				commercialProfiles: dataContext,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			nextState.hawkSoftFieldsToUpdate = {
				...hawkSoftFieldsToUpdate,
				commercialProfiles: dataContext,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			nextState.eclipseFieldsToUpdate = {
				...eclipseFieldsToUpdate,
				commercialProfiles: dataContext,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			nextState.qqFieldsToUpdate = {
				...qqFieldsToUpdate,
				commercialProfiles: dataContext,
			};
		}

		this.setState(nextState);
	};

	private onPersonalProfileChange = ({ dataContext }: ISelectOption<Api.PersonalProfiles>) => {
		const { integrationType } = this.props;
		const { amsFieldsToUpdate, eclipseFieldsToUpdate, hawkSoftFieldsToUpdate } = this.state;
		const nextState = { ...this.state };

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			nextState.amsFieldsToUpdate = {
				...amsFieldsToUpdate,
				personalProfiles: dataContext,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			nextState.hawkSoftFieldsToUpdate = {
				...hawkSoftFieldsToUpdate,
				personalProfiles: dataContext,
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			nextState.eclipseFieldsToUpdate = {
				...eclipseFieldsToUpdate,
				personalProfiles: dataContext,
			};
		}

		this.setState(nextState);
	};

	private onLifeHealthProfileChange = ({ dataContext }: ISelectOption<Api.LifeHealthProfiles>) => {
		const { integrationType } = this.props;
		const { hawkSoftFieldsToUpdate } = this.state;
		const nextState = { ...this.state };

		if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			nextState.hawkSoftFieldsToUpdate = {
				...hawkSoftFieldsToUpdate,
				lifeHealthProfiles: dataContext,
			};
		}

		this.setState(nextState);
	};

	private onWriteBackChange = (selectedOptions: ISelectOption<Api.WriteBackOption>[]) => {
		const { integrationType } = this.props;
		const { redtailFieldsToUpdate, amsFieldsToUpdate, clioFieldsToUpdate, qqFieldsToUpdate, hawkSoftFieldsToUpdate } =
			this.state;

		let nextState = { ...this.state };

		if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			nextState.redtailFieldsToUpdate = {
				...redtailFieldsToUpdate,
				writeOptions: {
					writeBackTypes: selectedOptions.map(opt => opt.dataContext),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			nextState.amsFieldsToUpdate = {
				...amsFieldsToUpdate,
				writeOptions: {
					writeBackTypes: selectedOptions.map(opt => opt.dataContext),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			nextState.clioFieldsToUpdate = {
				...clioFieldsToUpdate,
				writeOptions: {
					writeBackTypes: selectedOptions.map(opt => opt.dataContext),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			nextState.qqFieldsToUpdate = {
				...qqFieldsToUpdate,
				writeOptions: {
					writeBackTypes: selectedOptions.map(opt => opt.dataContext),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			nextState.hawkSoftFieldsToUpdate = {
				...hawkSoftFieldsToUpdate,
				writeOptions: {
					writeBackTypes: selectedOptions.map(opt => opt.dataContext),
				},
			};
		} else {
			nextState = this.state;
		}

		this.setState(nextState);
	};

	private onTagsDropdownClose = (selectedOptions: ISelectOption<ITagKeyFactOption>[]) => {
		const { integrationType } = this.props;
		const {
			amsFieldsToUpdate,
			clioFieldsToUpdate,
			hawkSoftFieldsToUpdate,
			eclipseFieldsToUpdate,
			redtailFieldsToUpdate,
			ezLynxFieldsToUpdate,
			epicFieldsToUpdate,
			qqFieldsToUpdate,
			xanatekFieldsToUpdate,
			donorPerfectFieldsToUpdate,
		} = this.state;
		const nextState = { ...this.state };

		const subStatusTagOption = hawkSoftFieldsToUpdate?.tagKeyFactOptions?.subStatusTagOption;

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			nextState.amsFieldsToUpdate = {
				...amsFieldsToUpdate,
				tagKeyFactOptions: {
					...amsFieldsToUpdate.tagKeyFactOptions,
					...this.updateAms360TagKeyFactOptions(selectedOptions),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			nextState.hawkSoftFieldsToUpdate = {
				...hawkSoftFieldsToUpdate,
				tagKeyFactOptions: {
					...hawkSoftFieldsToUpdate.tagKeyFactOptions,
					...this.updateHawkSoftTagKeyFactOptions(selectedOptions),
					subStatusTagOption, // this option should not be overwritten here
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			nextState.eclipseFieldsToUpdate = {
				...eclipseFieldsToUpdate,
				tagKeyFactOptions: {
					...eclipseFieldsToUpdate.tagKeyFactOptions,
					...this.updateEclipseTagKeyFactOptions(selectedOptions),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			nextState.redtailFieldsToUpdate = {
				...redtailFieldsToUpdate,
				tagKeyFactOptions: {
					...redtailFieldsToUpdate.tagKeyFactOptions,
					...this.updateRedtailTagKeyFactOptions(selectedOptions),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			nextState.clioFieldsToUpdate = {
				...clioFieldsToUpdate,
				tagKeyFactOptions: {
					...clioFieldsToUpdate.tagKeyFactOptions,
					...this.updateClioTagKeyFactOptions(selectedOptions),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
			nextState.ezLynxFieldsToUpdate = {
				...ezLynxFieldsToUpdate,
				tagKeyFactOptions: {
					...ezLynxFieldsToUpdate.tagKeyFactOptions,
					...this.updateEzlynxTagKeyFactOptions(selectedOptions),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
			nextState.epicFieldsToUpdate = {
				...epicFieldsToUpdate,
				tagKeyFactOptions: {
					...epicFieldsToUpdate.tagKeyFactOptions,
					...this.updateEpicTagKeyFactOptions(selectedOptions),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			nextState.qqFieldsToUpdate = {
				...qqFieldsToUpdate,
				tagKeyFactOptions: {
					...qqFieldsToUpdate.tagKeyFactOptions,
					...this.updateQQTagKeyFactOptions(selectedOptions),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
			nextState.xanatekFieldsToUpdate = {
				...xanatekFieldsToUpdate,
				tagKeyFactOptions: {
					...xanatekFieldsToUpdate.tagKeyFactOptions,
					...this.updateXanatekTagKeyFactOptions(selectedOptions),
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.DonorPerfect) {
			nextState.donorPerfectFieldsToUpdate = {
				...donorPerfectFieldsToUpdate,
				tagKeyFactOptions: {
					...donorPerfectFieldsToUpdate.tagKeyFactOptions,
					...this.updateDonorPerfectTagKeyFactOptions(selectedOptions),
				},
			};
		}

		this.setState(nextState);
	};

	private renderWriteBackTrigger = (selectedOptions: ISelectOption<Api.WriteBackOption | string>[]) => {
		const text = `${selectedOptions.map(option => option.text).join(' and ')}`;
		return <div className={css(styleSheet.writeBackTrigger)}>{text || 'None Selected'}</div>;
	};

	private getRedtailAvailableOptions = (type: RedtailAvailableOptions) => {
		const availableOptions = this.getAvailable(type);
		const blockedOptions = this.getBlocked(type);

		return availableOptions
			.filter(opt => !blockedOptions.includes(opt))
			.map(opt => this.renderRedtailAvailableOption(opt));
	};

	private renderAvailableOptionsTrigger = (selectedOptions: ISelectOption<string>[]) => {
		return `${selectedOptions.length || 'None'} selected`;
	};

	private renderRedtailAvailableOption = (option: string): ISelectOption<string> => {
		return {
			dataContext: option,
			id: `redtail-available-option-${option.toLowerCase().split(' ').join('-')}`,
			text: option,
			type: 'checkbox',
		};
	};

	private renderHawkSoftSubstatusOptionsTrigger = (selectedOptions: ISelectOption<string>[]) =>
		`${selectedOptions.length || 'None'} selected`;

	private updateAms360TagKeyFactOptions = (selectedOptions?: ISelectOption<ITagKeyFactOption>[]) => {
		const { integration: i } = this.props;
		const { amsFieldsToUpdate = { tagKeyFactOptions: {} } } = this.state;
		const integration = i ? (i as Api.IAms360Integration) : null;
		const updatedKeyFacts = {
			...integration.tagKeyFactOptions,
			...amsFieldsToUpdate.tagKeyFactOptions,
		};

		this.getAms360TagKeyFactOptions().forEach(option => {
			const tagName = option.dataContext.tagName as keyof Api.IAms360TagKeyFactOptions;
			const optionSelected = !!selectedOptions.find(o => o.dataContext.tagName === tagName);
			(updatedKeyFacts[tagName] as boolean) = optionSelected;
		});

		return updatedKeyFacts;
	};

	private updateHawkSoftTagKeyFactOptions = (selectedOptions?: ISelectOption<ITagKeyFactOption>[]) => {
		const { integration: i } = this.props;
		const { hawkSoftFieldsToUpdate = { tagKeyFactOptions: {} } } = this.state;
		const integration = i ? (i as Api.IHawkSoftIntegration) : null;
		const updatedKeyFacts = {
			...integration.tagKeyFactOptions,
			...hawkSoftFieldsToUpdate.tagKeyFactOptions,
		};

		this.getHawkSoftTagKeyFactOptions().forEach(option => {
			const tagName = option.dataContext.tagName as keyof Api.IHawkSoftTagKeyFactOptions;
			const optionSelected = !!selectedOptions.find(o => o.dataContext.tagName === tagName);
			(updatedKeyFacts[tagName] as boolean) = optionSelected;
		});

		return updatedKeyFacts;
	};

	private updateEpicTagKeyFactOptions = (selectedOptions?: ISelectOption<ITagKeyFactOption>[]) => {
		const { integration: i } = this.props;
		const { epicFieldsToUpdate = { tagKeyFactOptions: {} } } = this.state;
		const integration = i ? (i as Api.IEpicCsvIntegration) : null;
		const updatedKeyFacts = {
			...integration.tagKeyFactOptions,
			...epicFieldsToUpdate.tagKeyFactOptions,
		};

		this.getEpicTagKeyFactOptions().forEach(option => {
			const tagName = option.dataContext.tagName as keyof Api.IEpicTagKeyFactOptions;
			const optionSelected = !!selectedOptions.find(o => o.dataContext.tagName === tagName);
			(updatedKeyFacts[tagName] as boolean) = optionSelected;
		});

		return updatedKeyFacts;
	};

	private updateQQTagKeyFactOptions = (selectedOptions?: ISelectOption<ITagKeyFactOption>[]) => {
		const { integration: i } = this.props;
		const { qqFieldsToUpdate = { tagKeyFactOptions: {} } } = this.state;
		const integration = i ? (i as Api.IQQIntegration) : null;
		const updatedKeyFacts = {
			...integration.tagKeyFactOptions,
			...qqFieldsToUpdate.tagKeyFactOptions,
		};

		this.getQQTagKeyFactOptions().forEach(option => {
			const tagName = option.dataContext.tagName as keyof Api.IQQTagKeyFactOptions;
			const optionSelected = !!selectedOptions.find(o => o.dataContext.tagName === tagName);
			(updatedKeyFacts[tagName] as boolean) = optionSelected;
		});

		return updatedKeyFacts;
	};

	private updateXanatekTagKeyFactOptions = (selectedOptions?: ISelectOption<ITagKeyFactOption>[]) => {
		const { integration: i } = this.props;
		const { xanatekFieldsToUpdate = { tagKeyFactOptions: {} } } = this.state;
		const integration = i ? (i as Api.IGeneralCsvIntegration) : null;
		const updatedKeyFacts = {
			...integration.tagKeyFactOptions,
			...xanatekFieldsToUpdate.tagKeyFactOptions,
		};

		this.getXanatekTagKeyFactOptions().forEach(option => {
			const tagName = option.dataContext.tagName as keyof Api.IInsuranceTagKeyFactOptions;
			const optionSelected = !!selectedOptions.find(o => o.dataContext.tagName === tagName);
			(updatedKeyFacts[tagName] as boolean) = optionSelected;
		});

		return updatedKeyFacts;
	};

	private updateEclipseTagKeyFactOptions = (selectedOptions?: ISelectOption<ITagKeyFactOption>[]) => {
		const { integration: i } = this.props;
		const { eclipseFieldsToUpdate = { tagKeyFactOptions: {} } } = this.state;
		const integration = i ? (i as Api.IEclipseIntegration) : null;
		const updatedKeyFacts = {
			...integration.tagKeyFactOptions,
			...eclipseFieldsToUpdate.tagKeyFactOptions,
		};

		this.getEclipseTagKeyFactOptions().forEach(option => {
			const tagName = option.dataContext.tagName as keyof Api.IEclipseTagKeyFactOptions;
			const optionSelected = !!selectedOptions.find(o => o.dataContext.tagName === tagName);
			(updatedKeyFacts[tagName] as boolean) = optionSelected;
		});

		return updatedKeyFacts;
	};

	private updateRedtailTagKeyFactOptions = (selectedOptions?: ISelectOption<ITagKeyFactOption>[]) => {
		const { integration: i } = this.props;
		const { redtailFieldsToUpdate = { tagKeyFactOptions: {} } } = this.state;
		const integration = i ? (i as Api.IRedtailIntegration) : null;
		const updatedKeyFacts = {
			...integration.tagKeyFactOptions,
			...redtailFieldsToUpdate.tagKeyFactOptions,
		};

		this.getRedtailTagKeyFactOptions().forEach(option => {
			const tagName = option.dataContext.tagName as keyof Api.IRedtailTagKeyFactOptions;
			const optionSelected = !!selectedOptions.find(o => o.dataContext.tagName === tagName);
			(updatedKeyFacts[tagName] as boolean) = optionSelected;
		});

		return updatedKeyFacts;
	};

	private updateClioTagKeyFactOptions = (selectedOptions?: ISelectOption<ITagKeyFactOption>[]) => {
		const { integration: i } = this.props;
		const { clioFieldsToUpdate = { tagKeyFactOptions: {} } } = this.state;
		const integration = i ? (i as Api.IClioIntegration) : null;
		const updatedKeyFacts = {
			...integration.tagKeyFactOptions,
			...clioFieldsToUpdate.tagKeyFactOptions,
		};

		this.getClioTagKeyFactOptions().forEach(option => {
			const tagName = option.dataContext.tagName as keyof Api.IClioTagKeyFactOptions;
			const optionSelected = !!selectedOptions.find(o => o.dataContext.tagName === tagName);
			(updatedKeyFacts[tagName] as boolean) = optionSelected;
		});

		return updatedKeyFacts;
	};

	private updateEzlynxTagKeyFactOptions = (selectedOptions?: ISelectOption<ITagKeyFactOption>[]) => {
		const { integration: i } = this.props;
		const { ezLynxFieldsToUpdate = { tagKeyFactOptions: {} } } = this.state;
		const integration = i ? (i as Api.IEzLynxCsvIntegration) : null;
		const updatedKeyFacts = {
			...integration.tagKeyFactOptions,
			...ezLynxFieldsToUpdate.tagKeyFactOptions,
		};

		this.getEzlynxTagKeyFactOptions().forEach(option => {
			const tagName = option.dataContext.tagName as keyof Api.IEzLynxTagKeyFactOptions;
			const optionSelected = !!selectedOptions.find(o => o.dataContext.tagName === tagName);
			(updatedKeyFacts[tagName] as boolean) = optionSelected;
		});

		return updatedKeyFacts;
	};

	private updateDonorPerfectTagKeyFactOptions = (selectedOptions?: ISelectOption<ITagKeyFactOption>[]) => {
		const { integration: i } = this.props;
		const { donorPerfectFieldsToUpdate = { tagKeyFactOptions: {} } } = this.state;
		const integration = i as Api.IDonorPerfectIntegration;
		const updatedKeyFacts = {
			...integration.tagKeyFactOptions,
			...donorPerfectFieldsToUpdate.tagKeyFactOptions,
		};

		this.getDonorPerfectTagKeyFactOptions().forEach(option => {
			const tagName = option.dataContext.tagName as keyof Api.IDonorPerfectTagKeyFactOptions;
			const optionSelected = !!selectedOptions.find(o => o.dataContext.tagName === tagName);
			(updatedKeyFacts[tagName] as boolean) = optionSelected;
		});

		return updatedKeyFacts;
	};

	/** SETTINGS RENDERERS */
	private renderAms360LoginModal() {
		const { integration: i, integrationType } = this.props;
		const { isUserCredentialsModalOpened } = this.state;
		const integration = i ? (i as Api.IAms360Integration) : null;
		return integrationType === Api.ConfigurableIntegrationType.Ams360 ? (
			<Modal
				isOpen={isUserCredentialsModalOpened}
				onRequestClose={this.onCloseUserCredentialsModal}
				useDefaultHeader={true}
			>
				<div>
					Login ID:
					<TextInput
						className={css(styleSheet.textInput)}
						disabled={true}
						inputId='user-credentials-username'
						type='text'
						value={integration?.credentials.userName}
					/>
					Password:
					<TextInput
						className={css(styleSheet.textInput)}
						disabled={true}
						inputId='user-credentials-password'
						type='text'
						value={integration?.credentials.password}
					/>
				</div>
			</Modal>
		) : null;
	}

	private renderAms360UserCredentials() {
		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='AMS360 User Credentials'
				isEditMode={false}
			>
				<div>
					Hidden
					<button
						className={css(baseStyleSheet.brandLink, styleSheet.userCredentialsLink)}
						title='Show'
						onClick={this.onShowUserCredentialsModal}
					>
						Show
					</button>
				</div>
			</AccountDetailsInfoField>
		);
	}

	private renderBirthdayKeyFacts() {
		const { integration: i, integrationType } = this.props;
		const {
			amsFieldsToUpdate,
			clioFieldsToUpdate,
			hawkSoftFieldsToUpdate,
			eclipseFieldsToUpdate,
			epicFieldsToUpdate,
			isEditing,
			redtailFieldsToUpdate,
			ezLynxFieldsToUpdate,
			qqFieldsToUpdate,
			xanatekFieldsToUpdate,
		} = this.state;
		let fieldsToUpdate:
			| Partial<Api.IAms360Integration>
			| Partial<Api.IHawkSoftIntegration>
			| Partial<Api.IEclipseIntegration>
			| Partial<Api.IRedtailIntegration>
			| Partial<Api.IClioIntegration>
			| Partial<Api.IEpicCsvIntegration>
			| Partial<Api.IGeneralCsvIntegration>;
		let integration:
			| Api.IAms360Integration
			| Api.IHawkSoftIntegration
			| Api.IEclipseIntegration
			| Api.IRedtailIntegration
			| Api.IClioIntegration
			| Api.IEpicCsvIntegration
			| Api.IGeneralCsvIntegration;
		let onOptionClick: (option: ISelectOption<Api.BirthdayKeyFactDisplay>) => void;

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			fieldsToUpdate = amsFieldsToUpdate;
			integration = i ? (i as Api.IAms360Integration) : null;
			onOptionClick = this.onBirthdayKeyFactChange(Api.ConfigurableIntegrationType.Ams360);
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			fieldsToUpdate = hawkSoftFieldsToUpdate;
			integration = i ? (i as Api.IHawkSoftIntegration) : null;
			onOptionClick = this.onBirthdayKeyFactChange(Api.ConfigurableIntegrationType.HawkSoft);
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			fieldsToUpdate = eclipseFieldsToUpdate;
			integration = i ? (i as Api.IEclipseIntegration) : null;
			onOptionClick = this.onBirthdayKeyFactChange(Api.ConfigurableIntegrationType.Eclipse);
		} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			fieldsToUpdate = redtailFieldsToUpdate;
			integration = i ? (i as Api.IRedtailIntegration) : null;
			onOptionClick = this.onBirthdayKeyFactChange(Api.ConfigurableIntegrationType.Redtail);
		} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			fieldsToUpdate = clioFieldsToUpdate;
			integration = i ? (i as Api.IClioIntegration) : null;
			onOptionClick = this.onBirthdayKeyFactChange(Api.ConfigurableIntegrationType.Clio);
		} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
			fieldsToUpdate = ezLynxFieldsToUpdate;
			integration = i ? (i as Api.IEzLynxCsvIntegration) : null;
			onOptionClick = this.onBirthdayKeyFactChange(Api.ConfigurableIntegrationType.EzLynx);
		} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
			fieldsToUpdate = epicFieldsToUpdate;
			integration = i ? (i as Api.IEpicCsvIntegration) : null;
			onOptionClick = this.onBirthdayKeyFactChange(Api.ConfigurableIntegrationType.Epic);
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			fieldsToUpdate = qqFieldsToUpdate;
			integration = i ? (i as Api.IQQIntegration) : null;
			onOptionClick = this.onBirthdayKeyFactChange(Api.ConfigurableIntegrationType.QQ);
		} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
			fieldsToUpdate = xanatekFieldsToUpdate;
			integration = i ? (i as Api.IGeneralCsvIntegration) : null;
			onOptionClick = this.onBirthdayKeyFactChange(Api.ConfigurableIntegrationType.Xanatek);
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Birthday Key Facts'
				isEditMode={false}
			>
				{isEditing ? (
					<Select
						value={
							(
								BirthdayKeyFactsOptions.find(
									x =>
										x.dataContext ===
										(fieldsToUpdate?.tagKeyFactOptions?.birthdayKeyFactDisplay ??
											integration?.tagKeyFactOptions?.birthdayKeyFactDisplay)
								) || BirthdayKeyFactsOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = BirthdayKeyFactsOptions.find(o => o.id === value);
							if (option) onOptionClick(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										BirthdayKeyFactsOptions.find(
											x =>
												x.dataContext ===
												(fieldsToUpdate?.tagKeyFactOptions?.birthdayKeyFactDisplay ??
													integration?.tagKeyFactOptions?.birthdayKeyFactDisplay)
										) || BirthdayKeyFactsOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{BirthdayKeyFactsOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					(BirthdayKeyFactsOptions.find(x => x.dataContext === integration?.tagKeyFactOptions?.birthdayKeyFactDisplay)
						?.text ?? BirthdayKeyFactsOptions[0]?.text)
				)}
			</AccountDetailsInfoField>
		);
	}
	private renderSegmentByTags() {
		const { integration: i, integrationType } = this.props;
		const {
			amsFieldsToUpdate,
			clioFieldsToUpdate,
			hawkSoftFieldsToUpdate,
			eclipseFieldsToUpdate,
			isEditing,
			redtailFieldsToUpdate,
			qqFieldsToUpdate,
			xanatekFieldsToUpdate,
			showSegmentTagModal,
			donorPerfectFieldsToUpdate,
			epicFieldsToUpdate,
			ezLynxFieldsToUpdate,
		} = this.state;
		let fieldsToUpdate:
			| Partial<Api.IAms360Integration>
			| Partial<Api.IHawkSoftIntegration>
			| Partial<Api.IEclipseIntegration>
			| Partial<Api.IRedtailIntegration>
			| Partial<Api.IClioIntegration>
			| Partial<Api.IGeneralCsvIntegration>
			| Partial<Api.IDonorPerfectIntegration>
			| Partial<Api.IEzLynxCsvIntegration>
			| Partial<Api.IEpicCsvIntegration>;
		let integration:
			| Api.IAms360Integration
			| Api.IHawkSoftIntegration
			| Api.IEclipseIntegration
			| Api.IRedtailIntegration
			| Api.IClioIntegration
			| Api.IGeneralCsvIntegration
			| Api.IDonorPerfectIntegration
			| Api.IEzLynxCsvIntegration
			| Api.IEpicCsvIntegration;
		let onOptionClick: (option: ISelectOption<boolean>) => void;
		let onConfirm: (containsAll: boolean, tagValues: string[], exclude: boolean) => void;

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			fieldsToUpdate = amsFieldsToUpdate;
			integration = i ? (i as Api.IAms360Integration) : null;
			onOptionClick = this.onSegmentChange(Api.ConfigurableIntegrationType.Ams360);
			onConfirm = this.segmentByTagValuesConfirm(Api.ConfigurableIntegrationType.Ams360);
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			fieldsToUpdate = hawkSoftFieldsToUpdate;
			integration = i ? (i as Api.IHawkSoftIntegration) : null;
			onOptionClick = this.onSegmentChange(Api.ConfigurableIntegrationType.HawkSoft);
			onConfirm = this.segmentByTagValuesConfirm(Api.ConfigurableIntegrationType.HawkSoft);
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			fieldsToUpdate = eclipseFieldsToUpdate;
			integration = i ? (i as Api.IEclipseIntegration) : null;
			onOptionClick = this.onSegmentChange(Api.ConfigurableIntegrationType.Eclipse);
			onConfirm = this.segmentByTagValuesConfirm(Api.ConfigurableIntegrationType.Eclipse);
		} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			fieldsToUpdate = redtailFieldsToUpdate;
			integration = i ? (i as Api.IRedtailIntegration) : null;
			onOptionClick = this.onSegmentChange(Api.ConfigurableIntegrationType.Redtail);
			onConfirm = this.segmentByTagValuesConfirm(Api.ConfigurableIntegrationType.Redtail);
		} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			fieldsToUpdate = clioFieldsToUpdate;
			integration = i ? (i as Api.IClioIntegration) : null;
			onOptionClick = this.onSegmentChange(Api.ConfigurableIntegrationType.Clio);
			onConfirm = this.segmentByTagValuesConfirm(Api.ConfigurableIntegrationType.Clio);
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			fieldsToUpdate = qqFieldsToUpdate;
			integration = i ? (i as Api.IQQIntegration) : null;
			onOptionClick = this.onSegmentChange(Api.ConfigurableIntegrationType.QQ);
			onConfirm = this.segmentByTagValuesConfirm(Api.ConfigurableIntegrationType.QQ);
		} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
			fieldsToUpdate = xanatekFieldsToUpdate;
			integration = i ? (i as Api.IGeneralCsvIntegration) : null;
			onOptionClick = this.onSegmentChange(Api.ConfigurableIntegrationType.Xanatek);
			onConfirm = this.segmentByTagValuesConfirm(Api.ConfigurableIntegrationType.Xanatek);
		} else if (integrationType === Api.ConfigurableIntegrationType.DonorPerfect) {
			fieldsToUpdate = donorPerfectFieldsToUpdate;
			integration = i as Api.IDonorPerfectIntegration;
			onOptionClick = this.onSegmentChange(Api.ConfigurableIntegrationType.DonorPerfect);
			onConfirm = this.segmentByTagValuesConfirm(Api.ConfigurableIntegrationType.DonorPerfect);
		} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
			fieldsToUpdate = epicFieldsToUpdate;
			integration = i as Api.IEpicCsvIntegration;
			onOptionClick = this.onSegmentChange(Api.ConfigurableIntegrationType.Epic);
			onConfirm = this.segmentByTagValuesConfirm(Api.ConfigurableIntegrationType.Epic);
		} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
			fieldsToUpdate = ezLynxFieldsToUpdate;
			integration = i as Api.IEzLynxCsvIntegration;
			onOptionClick = this.onSegmentChange(Api.ConfigurableIntegrationType.EzLynx);
			onConfirm = this.segmentByTagValuesConfirm(Api.ConfigurableIntegrationType.EzLynx);
		}

		return (
			<>
				<AccountDetailsInfoField
					className={`${css(styleSheet.infoField)}`}
					editingStyles={[styleSheet.infoFieldEditing]}
					label='Segment by Tags'
					isEditMode={false}
				>
					{isEditing ? (
						<Select
							value={
								SegmentTagOptions[
									(fieldsToUpdate?.segmentByTagValues?.enabled ?? integration.segmentByTagValues?.enabled) ? 0 : 1
								]?.id
							}
							onValueChange={value => {
								const option = SegmentTagOptions.find(o => o.id === value);
								if (option) onOptionClick(option);
							}}
						>
							<SelectTrigger styles={styleSheet.selectContainer}>
								<span>
									{
										SegmentTagOptions[
											(fieldsToUpdate?.segmentByTagValues?.enabled ?? integration.segmentByTagValues?.enabled) ? 0 : 1
										]?.text
									}
								</span>
							</SelectTrigger>
							<SelectContent>
								{SegmentTagOptions.map(option => (
									<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : integration?.segmentByTagValues?.enabled ? (
						'Multiple Selected'
					) : (
						'None Selected'
					)}
				</AccountDetailsInfoField>
				{isEditing && (
					<button className={css(baseStyleSheet.brandLink, styleSheet.tagList)} onClick={this.onOpenSegmentTagLink}>
						<span>Edit Tag List</span>
					</button>
				)}
				<SimpleTagSelector
					isOpen={showSegmentTagModal}
					onRequestClose={this.onCloseSegmentTag}
					tags={this.mIntegrationsVM.getByType(integrationType)?.availableSegmentingTags}
					onConfirm={onConfirm}
					initialTags={fieldsToUpdate?.segmentByTagValues?.tagValues ?? integration?.segmentByTagValues?.tagValues}
					initialContainsAll={
						fieldsToUpdate?.segmentByTagValues?.containsAll ?? integration?.segmentByTagValues?.containsAll
					}
					initialExclude={fieldsToUpdate?.segmentByTagValues?.exclude ?? integration?.segmentByTagValues?.exclude}
				/>
			</>
		);
	}

	private onCloseSegmentTag = (open?: boolean) => {
		return this.setState({ showSegmentTagModal: open });
	};

	private onOpenSegmentTagLink = () => {
		return this.setState({ showSegmentTagModal: true });
	};

	private onSegmentChange = (integrationType: Api.ConfigurableIntegrationType) => (option: ISelectOption<boolean>) => {
		const {
			amsFieldsToUpdate,
			hawkSoftFieldsToUpdate,
			eclipseFieldsToUpdate,
			redtailFieldsToUpdate,
			clioFieldsToUpdate,
			qqFieldsToUpdate,
			xanatekFieldsToUpdate,
			donorPerfectFieldsToUpdate,
			epicFieldsToUpdate,
			ezLynxFieldsToUpdate,
		} = this.state;
		const nextState: IState = this.state;
		if (option.dataContext === true) {
			this.setState({ showSegmentTagModal: true });
			return;
		}

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			nextState.amsFieldsToUpdate = {
				...amsFieldsToUpdate,
				segmentByTagValues: {
					...amsFieldsToUpdate.segmentByTagValues,
					enabled: option.dataContext,
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			nextState.hawkSoftFieldsToUpdate = {
				...hawkSoftFieldsToUpdate,
				segmentByTagValues: {
					...hawkSoftFieldsToUpdate.segmentByTagValues,
					enabled: option.dataContext,
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			nextState.eclipseFieldsToUpdate = {
				...eclipseFieldsToUpdate,
				segmentByTagValues: {
					...eclipseFieldsToUpdate.segmentByTagValues,
					enabled: option.dataContext,
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			nextState.redtailFieldsToUpdate = {
				...redtailFieldsToUpdate,
				segmentByTagValues: {
					...redtailFieldsToUpdate.segmentByTagValues,
					enabled: option.dataContext,
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			nextState.clioFieldsToUpdate = {
				...clioFieldsToUpdate,
				segmentByTagValues: {
					...clioFieldsToUpdate.segmentByTagValues,
					enabled: option.dataContext,
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			nextState.qqFieldsToUpdate = {
				...qqFieldsToUpdate,
				segmentByTagValues: {
					...qqFieldsToUpdate.segmentByTagValues,
					enabled: option.dataContext,
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
			nextState.xanatekFieldsToUpdate = {
				...xanatekFieldsToUpdate,
				segmentByTagValues: {
					...xanatekFieldsToUpdate.segmentByTagValues,
					enabled: option.dataContext,
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.DonorPerfect) {
			nextState.donorPerfectFieldsToUpdate = {
				...donorPerfectFieldsToUpdate,
				segmentByTagValues: {
					...donorPerfectFieldsToUpdate.segmentByTagValues,
					enabled: option.dataContext,
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
			nextState.epicFieldsToUpdate = {
				...epicFieldsToUpdate,
				segmentByTagValues: {
					...epicFieldsToUpdate.segmentByTagValues,
					enabled: option.dataContext,
				},
			};
		} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
			nextState.ezLynxFieldsToUpdate = {
				...ezLynxFieldsToUpdate,
				segmentByTagValues: {
					...ezLynxFieldsToUpdate.segmentByTagValues,
					enabled: option.dataContext,
				},
			};
		}
		this.setState(nextState);
	};

	private segmentByTagValuesConfirm =
		(integrationType: Api.ConfigurableIntegrationType) =>
		(containsAll: boolean, tagValues: string[], exclude: boolean) => {
			const {
				amsFieldsToUpdate,
				hawkSoftFieldsToUpdate,
				eclipseFieldsToUpdate,
				redtailFieldsToUpdate,
				clioFieldsToUpdate,
				qqFieldsToUpdate,
				xanatekFieldsToUpdate,
				donorPerfectFieldsToUpdate,
				epicFieldsToUpdate,
				ezLynxFieldsToUpdate,
			} = this.state;
			const nextState: IState = this.state;
			if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
				nextState.amsFieldsToUpdate = {
					...amsFieldsToUpdate,
					segmentByTagValues: {
						...amsFieldsToUpdate.segmentByTagValues,
						containsAll,
						enabled: true,
						exclude,
						tagValues,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
				nextState.hawkSoftFieldsToUpdate = {
					...hawkSoftFieldsToUpdate,
					segmentByTagValues: {
						...hawkSoftFieldsToUpdate.segmentByTagValues,
						containsAll,
						enabled: true,
						exclude,
						tagValues,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
				nextState.eclipseFieldsToUpdate = {
					...eclipseFieldsToUpdate,
					segmentByTagValues: {
						...eclipseFieldsToUpdate.segmentByTagValues,
						containsAll,
						enabled: true,
						exclude,
						tagValues,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
				nextState.redtailFieldsToUpdate = {
					...redtailFieldsToUpdate,
					segmentByTagValues: {
						...redtailFieldsToUpdate.segmentByTagValues,
						containsAll,
						enabled: true,
						exclude,
						tagValues,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
				nextState.clioFieldsToUpdate = {
					...clioFieldsToUpdate,
					segmentByTagValues: {
						...clioFieldsToUpdate.segmentByTagValues,
						containsAll,
						enabled: true,
						exclude,
						tagValues,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
				nextState.qqFieldsToUpdate = {
					...qqFieldsToUpdate,
					segmentByTagValues: {
						...qqFieldsToUpdate.segmentByTagValues,
						containsAll,
						enabled: true,
						exclude,
						tagValues,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
				nextState.xanatekFieldsToUpdate = {
					...xanatekFieldsToUpdate,
					segmentByTagValues: {
						...xanatekFieldsToUpdate.segmentByTagValues,
						containsAll,
						enabled: true,
						exclude,
						tagValues,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.DonorPerfect) {
				nextState.donorPerfectFieldsToUpdate = {
					...donorPerfectFieldsToUpdate,
					segmentByTagValues: {
						...donorPerfectFieldsToUpdate.segmentByTagValues,
						containsAll,
						enabled: true,
						exclude,
						tagValues,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
				nextState.epicFieldsToUpdate = {
					...epicFieldsToUpdate,
					segmentByTagValues: {
						...epicFieldsToUpdate.segmentByTagValues,
						containsAll,
						enabled: true,
						exclude,
						tagValues,
					},
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
				nextState.ezLynxFieldsToUpdate = {
					...ezLynxFieldsToUpdate,
					segmentByTagValues: {
						...ezLynxFieldsToUpdate.segmentByTagValues,
						containsAll,
						enabled: true,
						exclude,
						tagValues,
					},
				};
			}
			nextState.showSegmentTagModal = false;
			this.setState(nextState);
		};

	private onIgnoreNicknamesOptionChange =
		(integrationType: Api.ConfigurableIntegrationType) => (option: ISelectOption<boolean>) => {
			const { ezLynxFieldsToUpdate, epicFieldsToUpdate, xanatekFieldsToUpdate } = this.state;

			const nextState: IState = this.state;

			if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
				nextState.ezLynxFieldsToUpdate = {
					...ezLynxFieldsToUpdate,
					ignoreContactNicknames: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
				nextState.epicFieldsToUpdate = {
					...epicFieldsToUpdate,
					ignoreContactNicknames: option.dataContext,
				};
			} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
				nextState.xanatekFieldsToUpdate = {
					...xanatekFieldsToUpdate,
					ignoreContactNicknames: option.dataContext,
				};
			}

			this.setState(nextState);
		};

	private renderSalutationAsFirstName() {
		const { integration: i, integrationType } = this.props;
		const { eclipseFieldsToUpdate, isEditing } = this.state;
		let fieldsToUpdate: Partial<Api.IEclipseIntegration>;
		let integration: Api.IEclipseIntegration;
		let onOptionClick: (option: ISelectOption<boolean>) => void;

		if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			fieldsToUpdate = eclipseFieldsToUpdate;
			integration = i ? (i as Api.IEclipseIntegration) : null;
			onOptionClick = this.onSalutationAsFirstNameChange(Api.ConfigurableIntegrationType.Eclipse);
		}

		return (
			<ConditionalAccess permission={AdminPermission.SupportLevel3}>
				<AccountDetailsInfoField
					className={`${css(styleSheet.infoField)}`}
					editingStyles={[styleSheet.infoFieldEditing]}
					label='Use Salutation as First Name'
					isEditMode={false}
				>
					{isEditing ? (
						<Select
							value={
								EnableOptions[
									(fieldsToUpdate?.useSalutationAsFirstName ?? integration?.useSalutationAsFirstName) ? 0 : 1
								]?.id
							}
							onValueChange={value => {
								const option = EnableOptions.find(o => o.id === value);
								if (option) onOptionClick(option);
							}}
						>
							<SelectTrigger styles={styleSheet.selectContainer}>
								<span>
									{
										EnableOptions[
											(fieldsToUpdate?.useSalutationAsFirstName ?? integration?.useSalutationAsFirstName) ? 0 : 1
										]?.text
									}
								</span>
							</SelectTrigger>
							<SelectContent>
								{EnableOptions.map(option => (
									<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : integration?.useSalutationAsFirstName ? (
						'Enabled'
					) : (
						'Disabled'
					)}
				</AccountDetailsInfoField>
			</ConditionalAccess>
		);
	}

	private renderPrimaryEmailOnly() {
		const { integration: i, integrationType } = this.props;
		const {
			amsFieldsToUpdate,
			clioFieldsToUpdate,
			hawkSoftFieldsToUpdate,
			eclipseFieldsToUpdate,
			isEditing,
			redtailFieldsToUpdate,
			ezLynxFieldsToUpdate,
			epicFieldsToUpdate,
			qqFieldsToUpdate,
			hubSpotFieldsToUpdate,
			xanatekFieldsToUpdate,
			donorPerfectFieldsToUpdate,
		} = this.state;
		let fieldsToUpdate:
			| Partial<Api.IAms360Integration>
			| Partial<Api.IHawkSoftIntegration>
			| Partial<Api.IEclipseIntegration>
			| Partial<Api.IRedtailIntegration>
			| Partial<Api.IClioIntegration>
			| Partial<Api.IEzLynxCsvIntegration>
			| Partial<Api.IHubSpotIntegration>
			| Partial<Api.IGeneralCsvIntegration>
			| Partial<Api.IDonorPerfectIntegration>;
		let integration:
			| Api.IAms360Integration
			| Api.IHawkSoftIntegration
			| Api.IEclipseIntegration
			| Api.IRedtailIntegration
			| Api.IClioIntegration
			| Api.IEzLynxCsvIntegration
			| Api.IGeneralCsvIntegration
			| Api.IDonorPerfectIntegration;
		let onOptionClick: (option: ISelectOption<boolean>) => void;

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			fieldsToUpdate = amsFieldsToUpdate;
			integration = i ? (i as Api.IAms360Integration) : null;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.Ams360);
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			fieldsToUpdate = hawkSoftFieldsToUpdate;
			integration = i ? (i as Api.IHawkSoftIntegration) : null;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.HawkSoft);
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			fieldsToUpdate = eclipseFieldsToUpdate;
			integration = i ? (i as Api.IEclipseIntegration) : null;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.Eclipse);
		} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			fieldsToUpdate = redtailFieldsToUpdate;
			integration = i ? (i as Api.IRedtailIntegration) : null;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.Redtail);
		} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			fieldsToUpdate = clioFieldsToUpdate;
			integration = i ? (i as Api.IClioIntegration) : null;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.Clio);
		} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
			fieldsToUpdate = ezLynxFieldsToUpdate;
			integration = i ? (i as Api.IEzLynxCsvIntegration) : null;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.EzLynx);
		} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
			fieldsToUpdate = epicFieldsToUpdate;
			integration = i ? (i as Api.IEpicCsvIntegration) : null;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.Epic);
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			fieldsToUpdate = qqFieldsToUpdate;
			integration = i ? (i as Api.IQQIntegration) : null;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.QQ);
		} else if (integrationType === Api.ConfigurableIntegrationType.HubSpot) {
			fieldsToUpdate = hubSpotFieldsToUpdate;
			integration = i ? (i as Api.IHubSpotIntegration) : null;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.HubSpot);
		} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
			fieldsToUpdate = xanatekFieldsToUpdate;
			integration = i ? (i as Api.IGeneralCsvIntegration) : null;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.Xanatek);
		} else if (integrationType === Api.ConfigurableIntegrationType.DonorPerfect) {
			fieldsToUpdate = donorPerfectFieldsToUpdate;
			integration = i as Api.IDonorPerfectIntegration;
			onOptionClick = this.onPrimaryEmailOnlyChange(Api.ConfigurableIntegrationType.DonorPerfect);
		}

		return (
			<ConditionalAccess permission={AdminPermission.ModifyAccount}>
				<AccountDetailsInfoField
					className={`${css(styleSheet.infoField)}`}
					editingStyles={[styleSheet.infoFieldEditing]}
					label='Primary Email Only'
					isEditMode={false}
					info='Caution when disabling this setting.'
				>
					{isEditing ? (
						<Select
							value={EnableOptions[(fieldsToUpdate?.primaryEmailOnly ?? integration?.primaryEmailOnly) ? 0 : 1]?.id}
							onValueChange={value => {
								const option = EnableOptions.find(o => o.id === value);
								if (option) onOptionClick(option);
							}}
						>
							<SelectTrigger styles={styleSheet.selectContainer}>
								<span>
									{EnableOptions[(fieldsToUpdate?.primaryEmailOnly ?? integration?.primaryEmailOnly) ? 0 : 1]?.text}
								</span>
							</SelectTrigger>
							<SelectContent>
								{EnableOptions.map(option => (
									<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : integration?.primaryEmailOnly ? (
						'Enabled'
					) : (
						'Disabled'
					)}
				</AccountDetailsInfoField>
			</ConditionalAccess>
		);
	}

	private renderUseForMeetingScheduling() {
		const { integration: i, integrationType } = this.props;
		const { redtailFieldsToUpdate, isEditing } = this.state;
		if (integrationType !== Api.ConfigurableIntegrationType.Redtail) {
			return null;
		}

		const fieldsToUpdate = redtailFieldsToUpdate;
		const integration = i ? (i as Api.IRedtailIntegration) : null;
		const capitalizedIntegrationName = integrationType.charAt(0).toUpperCase() + integrationType.slice(1);
		const onOptionClick = this.onUseForMeetingSchedulingChanged(Api.ConfigurableIntegrationType.Redtail);

		return (
			<ConditionalAccess permission={AdminPermission.ModifyAccount}>
				<AccountDetailsInfoField
					className={`${css(styleSheet.infoField)}`}
					editingStyles={[styleSheet.infoFieldEditing]}
					label='Use as Meeting Provider'
					isEditMode={false}
					info={`Only users who are connected to the ${capitalizedIntegrationName} integration will be able to use their ${capitalizedIntegrationName} Calendar.`}
				>
					{isEditing ? (
						<Select
							value={
								EnableOptions[(fieldsToUpdate?.useForMeetingScheduling ?? integration?.useForMeetingScheduling) ? 0 : 1]
									?.id
							}
							onValueChange={value => {
								const option = EnableOptions.find(o => o.id === value);
								if (option) onOptionClick(option);
							}}
						>
							<SelectTrigger styles={styleSheet.selectContainer}>
								<span>
									{
										EnableOptions[
											(fieldsToUpdate?.useForMeetingScheduling ?? integration?.useForMeetingScheduling) ? 0 : 1
										]?.text
									}
								</span>
							</SelectTrigger>
							<SelectContent>
								{EnableOptions.map(option => (
									<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : integration?.useForMeetingScheduling ? (
						'Enabled'
					) : (
						'Disabled'
					)}
				</AccountDetailsInfoField>
			</ConditionalAccess>
		);
	}

	private renderCol(items: JSX.Element[]) {
		return (
			<div className={css(styleSheet.cardGroupColumn)} key={uuid()}>
				{items}
			</div>
		);
	}

	private renderCommercialProfiles() {
		const { integration: i, integrationType } = this.props;
		const { amsFieldsToUpdate, eclipseFieldsToUpdate, hawkSoftFieldsToUpdate, qqFieldsToUpdate, isEditing } =
			this.state;
		let fieldsToUpdate:
			| Partial<Api.IAms360Integration>
			| Partial<Api.IHawkSoftIntegration>
			| Partial<Api.IEclipseIntegration>
			| Partial<Api.IQQIntegration>;
		let integration: Api.IAms360Integration | Api.IHawkSoftIntegration | Api.IEclipseIntegration | Api.IQQIntegration;

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			fieldsToUpdate = amsFieldsToUpdate;
			integration = i as Api.IAms360Integration;
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			fieldsToUpdate = hawkSoftFieldsToUpdate;
			integration = i as Api.IHawkSoftIntegration;
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			fieldsToUpdate = eclipseFieldsToUpdate;
			integration = i as Api.IEclipseIntegration;
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			fieldsToUpdate = qqFieldsToUpdate;
			integration = i as Api.IQQIntegration;
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Commercial Profiles'
				isEditMode={false}
			>
				{isEditing ? (
					<Select
						value={
							(
								CommercialProfileOptions.find(x => x.dataContext === fieldsToUpdate?.commercialProfiles) ??
								CommercialProfileOptions.find(x => x.dataContext === integration?.commercialProfiles) ??
								CommercialProfileOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = CommercialProfileOptions.find(o => o.id === value);
							if (option) this.onCommercialProfileChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										CommercialProfileOptions.find(x => x.dataContext === fieldsToUpdate?.commercialProfiles) ??
										CommercialProfileOptions.find(x => x.dataContext === integration?.commercialProfiles) ??
										CommercialProfileOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{CommercialProfileOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					(
						CommercialProfileOptions.find(x => x.dataContext === integration?.commercialProfiles) ??
						CommercialProfileOptions[0]
					)?.text
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderPersonalProfiles() {
		const { integration: i, integrationType } = this.props;
		const { amsFieldsToUpdate, eclipseFieldsToUpdate, hawkSoftFieldsToUpdate, isEditing } = this.state;
		let fieldsToUpdate:
			| Partial<Api.IAms360Integration>
			| Partial<Api.IHawkSoftIntegration>
			| Partial<Api.IEclipseIntegration>;
		let integration: Api.IAms360Integration | Api.IHawkSoftIntegration | Api.IEclipseIntegration;

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			fieldsToUpdate = amsFieldsToUpdate;
			integration = i as Api.IAms360Integration;
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			fieldsToUpdate = hawkSoftFieldsToUpdate;
			integration = i as Api.IHawkSoftIntegration;
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			fieldsToUpdate = eclipseFieldsToUpdate;
			integration = i as Api.IEclipseIntegration;
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Personal Profiles'
				isEditMode={false}
			>
				{isEditing ? (
					<Select
						value={
							(
								PersonalProfileOptions.find(x => x.dataContext === fieldsToUpdate?.personalProfiles) ??
								PersonalProfileOptions.find(x => x.dataContext === integration?.personalProfiles) ??
								PersonalProfileOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = PersonalProfileOptions.find(o => o.id === value);
							if (option) this.onPersonalProfileChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										PersonalProfileOptions.find(x => x.dataContext === fieldsToUpdate?.personalProfiles) ??
										PersonalProfileOptions.find(x => x.dataContext === integration?.personalProfiles) ??
										PersonalProfileOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{PersonalProfileOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					(
						PersonalProfileOptions.find(x => x.dataContext === integration?.personalProfiles) ??
						PersonalProfileOptions[0]
					)?.text
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderLifeHealthProfiles() {
		const { integration: i, integrationType } = this.props;
		const { hawkSoftFieldsToUpdate, isEditing } = this.state;
		let fieldsToUpdate: Partial<Api.IHawkSoftIntegration>;
		let integration: Api.IHawkSoftIntegration;

		if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			fieldsToUpdate = hawkSoftFieldsToUpdate;
			integration = i as Api.IHawkSoftIntegration;
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Life/Health Profiles'
				isEditMode={false}
			>
				{isEditing ? (
					<Select
						value={
							(
								LifeHealthProfileOptions.find(x => x.dataContext === fieldsToUpdate?.lifeHealthProfiles) ??
								LifeHealthProfileOptions.find(x => x.dataContext === integration?.lifeHealthProfiles) ??
								LifeHealthProfileOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = LifeHealthProfileOptions.find(o => o.id === value);
							if (option) this.onLifeHealthProfileChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										LifeHealthProfileOptions.find(x => x.dataContext === fieldsToUpdate?.lifeHealthProfiles) ??
										LifeHealthProfileOptions.find(x => x.dataContext === integration?.lifeHealthProfiles) ??
										LifeHealthProfileOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{LifeHealthProfileOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					(
						LifeHealthProfileOptions.find(x => x.dataContext === integration?.lifeHealthProfiles) ??
						LifeHealthProfileOptions[0]
					)?.text
				)}
			</AccountDetailsInfoField>
		);
	}

	private onCopyApiToken = () => {
		const { integration: i, integrationType } = this.props;
		let integration: Api.IEclipseIntegration | Api.IGeneralCsvIntegration;
		if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			integration = i as Api.IEclipseIntegration;
		} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
			integration = i as Api.IGeneralCsvIntegration;
		}

		if (integration.apiToken) {
			const done = copyToClipboard(integration.apiToken);
			if (done) {
				toast.success('Copied API key to clipboard!');
			} else {
				toast.error('Unable to copy to clipboard...');
			}
		} else {
			toast.error('No API key found.');
		}
	};

	private onCopyReportEmailAddress = () => {
		const { integration: i, integrationType } = this.props;
		let integration: Api.IEzLynxCsvIntegration;
		if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
			integration = i as Api.IEzLynxCsvIntegration;
		} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
			integration = i as Api.IEpicCsvIntegration;
		}

		if (integration.reportEmailAddress) {
			const done = copyToClipboard(integration.reportEmailAddress);
			if (done) {
				toast.success('Copied report email address to clipboard!');
			} else {
				toast.error('Unable to copy to clipboard...');
			}
		} else {
			toast.error('No report email address found.');
		}
	};

	private renderCopyApiToken() {
		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='API key'
				isEditMode={false}
			>
				<button className={css(styleSheet.copyApiTokenButton)} onClick={this.onCopyApiToken}>
					Copy API key
					<ClipboardIcon className={css(styleSheet.copyApiTokenButtonIcon)} />
				</button>
			</AccountDetailsInfoField>
		);
	}

	private renderReportEmailAddress() {
		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Report Email Address'
				isEditMode={false}
			>
				<button className={css(styleSheet.copyApiTokenButton)} onClick={this.onCopyReportEmailAddress}>
					Copy Report Email Address
					<ClipboardIcon className={css(styleSheet.copyApiTokenButtonIcon)} />
				</button>
			</AccountDetailsInfoField>
		);
	}

	private renderEditPen() {
		const { integration, onResyncLocal, onUnmergeContacts, onMapByResourceId, canUpdateMappingKey } = this.props;
		const { isEditing } = this.state;
		return (
			<div className={css(styleSheet.cardGroupColumn, styleSheet.cardGroupColumnEnd)}>
				{!isEditing ? (
					<div>
						<button
							className={`${css(styleSheet.actionEntities)}`}
							disabled={!!integration.locks?.ownershipRoleLock}
							onClick={this.onEditClick}
						>
							<span>
								<EditItemPenIcon />
							</span>
							<span>Edit</span>
						</button>
						<ConditionalAccess permission={AdminPermission.ModifyAccount}>
							<button
								className={`${css(styleSheet.actionEntities)}`}
								disabled={!!integration.locks?.ownershipRoleLock}
								onClick={onResyncLocal}
							>
								<span>
									<IntegrationIcon fill={brandPrimary} />
								</span>
								<span>Resync</span>
							</button>
						</ConditionalAccess>
						<ConditionalAccess permission={AdminPermission.SupportLevel3} or={AdminPermission.OnlyForDevelopers}>
							<button
								className={`${css(styleSheet.actionEntities)}`}
								disabled={!!integration.locks?.ownershipRoleLock}
								onClick={onUnmergeContacts}
							>
								<span>
									<UnmergeIcon />{' '}
								</span>
								<span>Unmerge Contacts</span>
							</button>
						</ConditionalAccess>
						{canUpdateMappingKey ? (
							<ConditionalAccess permission={AdminPermission.SupportLevel3}>
								<button
									className={`${css(styleSheet.actionEntities)}`}
									disabled={!!integration.locks?.ownershipRoleLock}
									onClick={onMapByResourceId}
								>
									<span>
										<MergeIcon />
									</span>
									<span>Map By Resource ID</span>
								</button>
							</ConditionalAccess>
						) : null}
					</div>
				) : (
					<div className={`${css(styleSheet.buttonContainer)} button-container`}>
						<div>
							<span className={css(baseStyleSheet.ctaButtonSmall)} onClick={this.onSaveClick}>
								Save
							</span>
							<span
								style={{ marginLeft: 10 }}
								className={css(baseStyleSheet.ctaButtonReverseSmall)}
								onClick={this.clearFieldsToUpdate}
							>
								Cancel
							</span>
						</div>
						<div>
							<button className={`${css(styleSheet.actionEntities)}`} onClick={onResyncLocal}>
								<span>
									<IntegrationIcon fill={brandPrimary} />
								</span>
								<span>Resync</span>
							</button>
						</div>
					</div>
				)}
			</div>
		);
	}

	private onFinancialReviewKeyFactChange =
		(integrationType: Api.ConfigurableIntegrationType) =>
		(option: ISelectOption<Api.FinancialReviewKeyFactOptions | 'Disabled'>) => {
			const { redtailFieldsToUpdate } = this.state;

			if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
				const value = option.dataContext === 'Disabled' ? null : option.dataContext;

				this.setState({
					redtailFieldsToUpdate: {
						...redtailFieldsToUpdate,
						tagKeyFactOptions: {
							...redtailFieldsToUpdate.tagKeyFactOptions,
							financialReviewOption: {
								enabled: !!value,
								property: value,
							},
						},
					},
				});
			}
		};

	private renderFinancialReviewKeyFacts() {
		const { integration: i, integrationType } = this.props;
		const { isEditing, redtailFieldsToUpdate } = this.state;
		let fieldsToUpdate: Partial<Api.IRedtailIntegration>;
		let integration: Api.IRedtailIntegration;
		let onOptionClick: (option: ISelectOption<Api.FinancialReviewKeyFactOptions | 'Disabled'>) => void;

		if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			fieldsToUpdate = redtailFieldsToUpdate;
			integration = i ? (i as Api.IRedtailIntegration) : null;
			onOptionClick = this.onFinancialReviewKeyFactChange(Api.ConfigurableIntegrationType.Redtail);
		}

		const isDisabled =
			(!fieldsToUpdate?.tagKeyFactOptions?.financialReviewOption &&
				!integration?.tagKeyFactOptions?.financialReviewOption?.enabled) ||
			(fieldsToUpdate?.tagKeyFactOptions?.financialReviewOption &&
				fieldsToUpdate.tagKeyFactOptions.financialReviewOption?.property === null);
		const notEditableText = isDisabled
			? FinancialReviewOptions.find(x => x.dataContext === 'Disabled')?.text
			: (FinancialReviewOptions.find(
					x => x.dataContext === integration?.tagKeyFactOptions?.financialReviewOption?.property
				)?.text ?? FinancialReviewOptions.find(x => x.dataContext === 'Disabled')?.text);

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Financial Review Key Facts'
				isEditMode={isEditing}
			>
				{isEditing ? (
					<Select
						value={
							(
								FinancialReviewOptions.find(x => {
									return isDisabled
										? x.dataContext === 'Disabled'
										: x.dataContext ===
												(fieldsToUpdate?.tagKeyFactOptions?.financialReviewOption?.property ||
													integration?.tagKeyFactOptions?.financialReviewOption?.property);
								}) ?? FinancialReviewOptions[FinancialReviewOptions.length - 1]
							)?.id
						}
						onValueChange={value => {
							const option = FinancialReviewOptions.find(o => o.id === value);
							if (option) onOptionClick(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										FinancialReviewOptions.find(x => {
											return isDisabled
												? x.dataContext === 'Disabled'
												: x.dataContext ===
														(fieldsToUpdate?.tagKeyFactOptions?.financialReviewOption?.property ||
															integration?.tagKeyFactOptions?.financialReviewOption?.property);
										}) ?? FinancialReviewOptions[FinancialReviewOptions.length - 1]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{FinancialReviewOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : notEditableText === 'Disable' ? (
					'Disabled'
				) : (
					notEditableText
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderIDs() {
		const { accountId, integration, integrationType } = this.props;

		let label: string;

		switch (integrationType) {
			case Api.ConfigurableIntegrationType.Ams360:
				label = 'Agency ID(s)';
				break;
			case Api.ConfigurableIntegrationType.HawkSoft:
				label = 'HawkSoft ID(s)';
				break;
			case Api.ConfigurableIntegrationType.Eclipse:
				label = 'Eclipse ID(s)';
				break;
			case Api.ConfigurableIntegrationType.EzLynx:
				label = 'EzLynx ID(s)';
				break;
			case Api.ConfigurableIntegrationType.Epic:
				label = 'Epic ID(s)';
				break;
			case Api.ConfigurableIntegrationType.QQ:
				label = 'QQCatalyst ID(s)';
				break;
			default:
				break;
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label={label}
				isEditMode={false}
			>
				{integration.agencyIds
					? integration.agencyIds.map(agencyId => {
							return <div key={accountId}>{agencyId}</div>;
						})
					: integration.agencyId}
			</AccountDetailsInfoField>
		);
	}

	private renderImportInactiveClients() {
		const { integration: i, integrationType } = this.props;
		const { amsFieldsToUpdate, isEditing } = this.state;
		let fieldsToUpdate: Partial<Api.IAms360Integration>;
		let integration: Api.IAms360Integration;
		let onOptionClick: (option: ISelectOption<boolean>) => void;

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			fieldsToUpdate = amsFieldsToUpdate;
			integration = i ? (i as Api.IAms360Integration) : null;
			onOptionClick = this.onImportInactiveClientsChange(Api.ConfigurableIntegrationType.Ams360);
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Import Inactive Clients'
				isEditMode={isEditing}
			>
				{isEditing ? (
					<Select
						value={
							EnableOptions[(fieldsToUpdate?.inactiveClientsEnabled ?? integration?.inactiveClientsEnabled) ? 0 : 1]?.id
						}
						onValueChange={value => {
							const option = EnableOptions.find(o => o.id === value);
							if (option) onOptionClick(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									EnableOptions[(fieldsToUpdate?.inactiveClientsEnabled ?? integration?.inactiveClientsEnabled) ? 0 : 1]
										?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{EnableOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : integration?.inactiveClientsEnabled ? (
					'Enabled'
				) : (
					'Disabled'
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderMappingKey() {
		const { integration } = this.props;
		return (
			<AccountDetailsInfoField className={`${css(styleSheet.infoField)}`} label='Mapping Key' isEditMode={false}>
				{integration?.preferredMappingKey}
			</AccountDetailsInfoField>
		);
	}

	private renderLastExtractDate() {
		const { integration, integrationType } = this.props;

		let value: string;

		if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			/**
			 * If both lastExtractDate and lastLoadDate do not exist, we have not received data from Eclipse yet.
			 *
			 * If either lastExtractDate or lastLoadDate exist, and the other does not, process is in progress.
			 */
			value =
				!integration?.lastExtractDate && !integration?.lastLoadDate
					? '--'
					: integration?.lastExtractDate
						? moment(integration.lastExtractDate).format(dateFormat)
						: 'In Progress';
		} else {
			value = integration?.lastExtractDate ? moment(integration.lastExtractDate).format(dateFormat) : 'In Progress';
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Last Extract Date'
				isEditMode={false}
			>
				{value}
			</AccountDetailsInfoField>
		);
	}

	private renderLastLoadDate() {
		const { integration, integrationType } = this.props;

		let value: string;

		if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			/**
			 * If both lastExtractDate and lastLoadDate do not exist, we have not received data from Eclipse yet.
			 *
			 * If either lastExtractDate or lastLoadDate exist, and the other does not, process is in progress.
			 */
			value =
				!integration?.lastExtractDate && !integration?.lastLoadDate
					? '--'
					: integration?.lastLoadDate
						? moment(integration.lastLoadDate).format(dateFormat)
						: 'In Progress';
		} else {
			value = integration?.lastLoadDate ? moment(integration.lastLoadDate).format(dateFormat) : 'In Progress';
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Last Load Date'
				isEditMode={false}
			>
				{value}
			</AccountDetailsInfoField>
		);
	}

	private renderLastNotificationReceived() {
		const { integration: i } = this.props;
		const integration = i ? (i as Api.IAms360Integration) : null;
		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Last Notification Received'
				isEditMode={false}
			>
				{integration?.lastNotificationReceivedDate
					? moment(integration.lastNotificationReceivedDate).format(dateFormat)
					: 'Not Received'}
			</AccountDetailsInfoField>
		);
	}

	private renderMappingInitials() {
		const { integration, integrationType, onRenderMapping } = this.props;

		const supportedIntegrations = [
			Api.ConfigurableIntegrationType.Ams360,
			Api.ConfigurableIntegrationType.HawkSoft,
			Api.ConfigurableIntegrationType.Eclipse,
			Api.ConfigurableIntegrationType.EzLynx,
			Api.ConfigurableIntegrationType.Epic,
			Api.ConfigurableIntegrationType.QQ,
			Api.ConfigurableIntegrationType.HubSpot,
			Api.ConfigurableIntegrationType.Xanatek,
			Api.ConfigurableIntegrationType.DonorPerfect,
		];
		const isSupportedIntegration = supportedIntegrations.indexOf(integrationType) !== -1;

		const isRedTail =
			integrationType === Api.ConfigurableIntegrationType.Redtail &&
			(integration as Api.IRedtailIntegration)?.ownershipRole !==
				Api.RedtailIntegrationOwnershipRole.FirstObservedUserFromContact;

		return isSupportedIntegration || isRedTail ? (
			<>
				<div
					className={`${integration.locks?.ownershipRoleLock ? css(styleSheet.disabled) : ''} ${
						integrationType === Api.ConfigurableIntegrationType.Redtail ? css(styleSheet.redtailMapping) : ''
					}`}
				>
					<b className={css(baseStyleSheet.fontBold)}>
						{`We've synced your data, and found the following users on the account. Please match the ${
							integrationType === Api.ConfigurableIntegrationType.Redtail
								? 'advisor'
								: integrationType === Api.ConfigurableIntegrationType.QQ ||
										integrationType === Api.ConfigurableIntegrationType.DonorPerfect
									? 'name'
									: 'initials'
						} with the user you'd like to add to Levitate by entering their email.`}
					</b>
				</div>
				{onRenderMapping ? onRenderMapping() : null}
			</>
		) : null;
	}

	private renderMappingBy() {
		const { integration, integrationType } = this.props;
		const {
			amsFieldsToUpdate,
			hawkSoftFieldsToUpdate,
			eclipseFieldsToUpdate,
			redtailFieldsToUpdate,
			epicFieldsToUpdate,
			qqFieldsToUpdate,
			xanatekFieldsToUpdate,
			donorPerfectFieldsToUpdate,
			isEditing,
		} = this.state;

		let select: JSX.Element;

		switch (integrationType) {
			case Api.ConfigurableIntegrationType.Ams360:
				select = (
					<Select
						value={
							(
								AmsMappingOptions.find(
									x =>
										x.dataContext ===
										(amsFieldsToUpdate?.ownershipRole ?? (integration as Api.IAms360Integration)?.ownershipRole)
								) || AmsMappingOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = AmsMappingOptions.find(o => o.id === value);
							if (option) this.onAmsMappingChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										AmsMappingOptions.find(
											x =>
												x.dataContext ===
												(amsFieldsToUpdate?.ownershipRole ?? (integration as Api.IAms360Integration)?.ownershipRole)
										) || AmsMappingOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{AmsMappingOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);
				break;
			case Api.ConfigurableIntegrationType.HawkSoft:
				select = (
					<Select
						value={
							(
								BaseMappingOptions.find(
									x =>
										x.dataContext ===
										(hawkSoftFieldsToUpdate?.ownershipRole ?? (integration as Api.IHawkSoftIntegration)?.ownershipRole)
								) || BaseMappingOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = BaseMappingOptions.find(o => o.id === value);
							if (option) this.onHawkSoftMappingChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										BaseMappingOptions.find(
											x =>
												x.dataContext ===
												(hawkSoftFieldsToUpdate?.ownershipRole ??
													(integration as Api.IHawkSoftIntegration)?.ownershipRole)
										) || BaseMappingOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{BaseMappingOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);
				break;
			case Api.ConfigurableIntegrationType.Eclipse:
				select = (
					<Select
						value={
							(
								BaseMappingOptions.find(
									x =>
										x.dataContext ===
										(eclipseFieldsToUpdate?.ownershipRole ?? (integration as Api.IEclipseIntegration)?.ownershipRole)
								) || BaseMappingOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = BaseMappingOptions.find(o => o.id === value);
							if (option) this.onEclipseMappingChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										BaseMappingOptions.find(
											x =>
												x.dataContext ===
												(eclipseFieldsToUpdate?.ownershipRole ??
													(integration as Api.IEclipseIntegration)?.ownershipRole)
										) || BaseMappingOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{BaseMappingOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);
				break;
			case Api.ConfigurableIntegrationType.Xanatek:
				select = (
					<Select
						value={
							(
								BaseMappingOptions.find(
									x =>
										x.dataContext ===
										(xanatekFieldsToUpdate?.ownershipRole ?? (integration as Api.IGeneralCsvIntegration)?.ownershipRole)
								) || BaseMappingOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = BaseMappingOptions.find(o => o.id === value);
							if (option) this.onXanatekMappingChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										BaseMappingOptions.find(
											x =>
												x.dataContext ===
												(xanatekFieldsToUpdate?.ownershipRole ??
													(integration as Api.IGeneralCsvIntegration)?.ownershipRole)
										) || BaseMappingOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{BaseMappingOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);
				break;
			case Api.ConfigurableIntegrationType.Redtail:
				select = (
					<Select
						value={
							(
								RedtailOwnershipRoleOptions.find(
									x =>
										x.dataContext ===
										(redtailFieldsToUpdate?.ownershipRole ?? (integration as Api.IRedtailIntegration)?.ownershipRole)
								) || RedtailOwnershipRoleOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = RedtailOwnershipRoleOptions.find(o => o.id === value);
							if (option) this.onRedtailMappingChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										RedtailOwnershipRoleOptions.find(
											x =>
												x.dataContext ===
												(redtailFieldsToUpdate?.ownershipRole ??
													(integration as Api.IRedtailIntegration)?.ownershipRole)
										) || RedtailOwnershipRoleOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{RedtailOwnershipRoleOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);
				break;
			case Api.ConfigurableIntegrationType.Epic:
				select = (
					<Select
						value={
							(
								BaseMappingOptions.find(
									x =>
										x.dataContext ===
										(epicFieldsToUpdate?.ownershipRole ?? (integration as Api.IEpicCsvIntegration)?.ownershipRole)
								) || BaseMappingOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = BaseMappingOptions.find(o => o.id === value);
							if (option) this.onEpicMappingChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										BaseMappingOptions.find(
											x =>
												x.dataContext ===
												(epicFieldsToUpdate?.ownershipRole ?? (integration as Api.IEpicCsvIntegration)?.ownershipRole)
										) || BaseMappingOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{BaseMappingOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);
				break;
			case Api.ConfigurableIntegrationType.QQ:
				select = (
					<Select
						value={
							(
								BaseMappingOptions.find(
									x =>
										x.dataContext ===
										(qqFieldsToUpdate?.ownershipRole ?? (integration as Api.IQQIntegration)?.ownershipRole)
								) || BaseMappingOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = BaseMappingOptions.find(o => o.id === value);
							if (option) this.onQQMappingChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										BaseMappingOptions.find(
											x =>
												x.dataContext ===
												(qqFieldsToUpdate?.ownershipRole ?? (integration as Api.IQQIntegration)?.ownershipRole)
										) || BaseMappingOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{BaseMappingOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);
				break;
			case Api.ConfigurableIntegrationType.DonorPerfect:
				select = (
					<Select
						value={
							(
								DonorPerfectOwnershipRoleOptions.find(
									x =>
										x.dataContext ===
										(donorPerfectFieldsToUpdate?.ownershipRole ??
											(integration as Api.IDonorPerfectIntegration)?.ownershipRole)
								) || DonorPerfectOwnershipRoleOptions[0]
							)?.id
						}
						onValueChange={value => {
							const option = DonorPerfectOwnershipRoleOptions.find(o => o.id === value);
							if (option) this.onDonorPerfectMappingChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									(
										DonorPerfectOwnershipRoleOptions.find(
											x =>
												x.dataContext ===
												(donorPerfectFieldsToUpdate?.ownershipRole ??
													(integration as Api.IDonorPerfectIntegration)?.ownershipRole)
										) || DonorPerfectOwnershipRoleOptions[0]
									)?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{DonorPerfectOwnershipRoleOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);
				break;
			default:
				break;
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Mapping By'
				isEditMode={false}
			>
				{isEditing ? select : this.getOwnershipRole()}
			</AccountDetailsInfoField>
		);
	}

	private renderNonClients() {
		const { integration: i } = this.props;
		const { clioFieldsToUpdate, isEditing } = this.state;
		const integration = i as Api.IClioIntegration;
		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Non-Clients'
				isEditMode={isEditing}
			>
				{isEditing ? (
					<Select
						value={EnableOptions[(clioFieldsToUpdate.nonClients ?? integration.nonClients) ? 0 : 1]?.id}
						onValueChange={value => {
							const option = EnableOptions.find(o => o.id === value);
							if (option) this.onNonClientOptionChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>{EnableOptions[(clioFieldsToUpdate.nonClients ?? integration.nonClients) ? 0 : 1]?.text}</span>
						</SelectTrigger>
						<SelectContent>
							{EnableOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					EnableOptions[(clioFieldsToUpdate.nonClients ?? integration.nonClients) ? 0 : 1].text
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderRelatedContacts() {
		const { integration: i } = this.props;
		const { clioFieldsToUpdate, isEditing } = this.state;
		const integration = i as Api.IClioIntegration;
		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Related Contacts'
				isEditMode={isEditing}
			>
				{isEditing ? (
					<Select
						value={EnableOptions[(clioFieldsToUpdate.relatedContacts ?? integration.relatedContacts) ? 0 : 1]?.id}
						onValueChange={value => {
							const option = EnableOptions.find(o => o.id === value);
							if (option) this.onRelatedContactsOptionChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{EnableOptions[(clioFieldsToUpdate.relatedContacts ?? integration.relatedContacts) ? 0 : 1]?.text}
							</span>
						</SelectTrigger>
						<SelectContent>
							{EnableOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					EnableOptions[(clioFieldsToUpdate.relatedContacts ?? integration.relatedContacts) ? 0 : 1].text
				)}
			</AccountDetailsInfoField>
		);
	}

	// does not use the same tags, so made more sense to have separate method
	private renderClioOwnershipTag() {
		const { integration: i } = this.props;
		const { clioFieldsToUpdate, isEditing } = this.state;
		const integration = i as Api.IClioIntegration;

		const selectedByDefault =
			ClioOwnershipRoleOptions.find(o => o.dataContext === clioFieldsToUpdate.ownershipRole) ??
			ClioOwnershipRoleOptions.find(o => o.dataContext === integration.ownershipRole);

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Ownership Role'
				isEditMode={isEditing}
			>
				{isEditing ? (
					<Select
						value={selectedByDefault?.id}
						onValueChange={value => {
							const option = ClioOwnershipRoleOptions.find(o => o.id === value);
							if (option) this.onClioOwnershipRoleChange(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>{selectedByDefault?.text}</span>
						</SelectTrigger>
						<SelectContent>
							{ClioOwnershipRoleOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					(selectedByDefault.text ?? ClioOwnershipRoleOptions[0].text)
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderClioAttorneyTagSettings(attorneyRole: Api.ClioIntegrationOwnershipRole) {
		const { integration: i } = this.props;
		const { clioFieldsToUpdate, isEditing } = this.state;
		const integration = i as Api.IClioIntegration;
		const selectedByDefault =
			attorneyRole === Api.ClioIntegrationOwnershipRole.OriginatingAttorney
				? (ClioAttorneyTagOptions.find(
						o => o.dataContext === clioFieldsToUpdate.tagKeyFactOptions?.originatingAttorneyTagOption
					) ??
					ClioAttorneyTagOptions.find(
						o => o.dataContext === integration.tagKeyFactOptions?.originatingAttorneyTagOption
					))
				: (ClioAttorneyTagOptions.find(
						o => o.dataContext === clioFieldsToUpdate.tagKeyFactOptions?.responsibleAttorneyTagOption
					) ??
					ClioAttorneyTagOptions.find(
						o => o.dataContext === integration.tagKeyFactOptions?.responsibleAttorneyTagOption
					));

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label={`${
					attorneyRole === Api.ClioIntegrationOwnershipRole.OriginatingAttorney ? 'Originating' : 'Responsible'
				} Attorney Tag Options`}
				isEditMode={isEditing}
			>
				{isEditing ? (
					<Select
						value={selectedByDefault?.id}
						onValueChange={value => {
							const option = ClioAttorneyTagOptions.find(o => o.id === value);
							if (option) this.onClioAttorneyTagSettingsChange(attorneyRole)(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>{selectedByDefault?.text}</span>
						</SelectTrigger>
						<SelectContent>
							{ClioAttorneyTagOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					(selectedByDefault.text ?? ClioAttorneyTagOptions[0].text)
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderOwnershipTag() {
		const { integration: i, integrationType } = this.props;
		const {
			amsFieldsToUpdate,
			hawkSoftFieldsToUpdate,
			eclipseFieldsToUpdate,
			epicFieldsToUpdate,
			qqFieldsToUpdate,
			xanatekFieldsToUpdate,
			isEditing,
		} = this.state;
		let fieldsToUpdate:
			| Partial<Api.IAms360Integration>
			| Partial<Api.IHawkSoftIntegration>
			| Partial<Api.IEclipseIntegration>
			| Partial<Api.IEpicCsvIntegration>
			| Partial<Api.IQQIntegration>
			| Partial<Api.IGeneralCsvIntegration>;
		let integration:
			| Api.IAms360Integration
			| Api.IHawkSoftIntegration
			| Api.IEclipseIntegration
			| Api.IEpicCsvIntegration
			| Api.IQQIntegration
			| Api.IGeneralCsvIntegration;
		let onOptionClick: (option: ISelectOption<boolean>) => void;

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			fieldsToUpdate = amsFieldsToUpdate;
			integration = i ? (i as Api.IAms360Integration) : null;
			onOptionClick = this.onOwnershipRoleChange(Api.ConfigurableIntegrationType.Ams360);
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			fieldsToUpdate = hawkSoftFieldsToUpdate;
			integration = i ? (i as Api.IHawkSoftIntegration) : null;
			onOptionClick = this.onOwnershipRoleChange(Api.ConfigurableIntegrationType.HawkSoft);
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			fieldsToUpdate = eclipseFieldsToUpdate;
			integration = i ? (i as Api.IEclipseIntegration) : null;
			onOptionClick = this.onOwnershipRoleChange(Api.ConfigurableIntegrationType.Eclipse);
		} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
			fieldsToUpdate = epicFieldsToUpdate;
			integration = i ? (i as Api.IEpicCsvIntegration) : null;
			onOptionClick = this.onOwnershipRoleChange(Api.ConfigurableIntegrationType.Epic);
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			fieldsToUpdate = qqFieldsToUpdate;
			integration = i ? (i as Api.IQQIntegration) : null;
			onOptionClick = this.onOwnershipRoleChange(Api.ConfigurableIntegrationType.QQ);
		} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
			fieldsToUpdate = xanatekFieldsToUpdate;
			integration = i ? (i as Api.IGeneralCsvIntegration) : null;
			onOptionClick = this.onOwnershipRoleChange(Api.ConfigurableIntegrationType.Xanatek);
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Ownership Tag'
				isEditMode={isEditing}
			>
				{isEditing ? (
					<Select
						value={
							OwnershipRoleOptions[
								// FOLLOWUP: Resolve

								(fieldsToUpdate?.tagKeyFactOptions?.displayBothOwnershipRoleTags ??
								// FOLLOWUP: Resolve

								integration?.tagKeyFactOptions?.displayBothOwnershipRoleTags)
									? 0
									: 1
							]?.id
						}
						onValueChange={value => {
							const option = OwnershipRoleOptions.find(o => o.id === value);
							if (option) onOptionClick(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									OwnershipRoleOptions[
										(fieldsToUpdate?.tagKeyFactOptions?.displayBothOwnershipRoleTags ??
										integration?.tagKeyFactOptions?.displayBothOwnershipRoleTags)
											? 0
											: 1
									]?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{OwnershipRoleOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : // FOLLOWUP: Resolve

				(fieldsToUpdate?.tagKeyFactOptions?.displayBothOwnershipRoleTags ??
					// FOLLOWUP: Resolve

					integration?.tagKeyFactOptions?.displayBothOwnershipRoleTags) ? (
					'Both Producer & CSR'
				) : (
					'Just Ownership'
				)}
			</AccountDetailsInfoField>
		);
	}

	private onPrimaryOwnerOptionClick = (option: ISelectOption<AccountUserViewModel>) => {
		const { clioFieldsToUpdate, redtailFieldsToUpdate } = this.state;
		const { integrationType } = this.props;
		if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			this.setState({
				clioFieldsToUpdate: {
					...clioFieldsToUpdate,
					primaryUser: {
						levitateUserId: option.dataContext.id,
					},
				},
			});
		}
		if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			this.setState({
				redtailFieldsToUpdate: {
					...redtailFieldsToUpdate,
					primaryUser: {
						levitateUserId: option.dataContext.id,
					},
				},
			});
		}
	};

	private renderPrimaryUserId() {
		const { integration: i, integrationType } = this.props;
		const { clioFieldsToUpdate, isEditing, redtailFieldsToUpdate } = this.state;
		const integration =
			integrationType === Api.ConfigurableIntegrationType.Clio
				? (i as Api.IClioIntegration)
				: (i as Api.IRedtailIntegration);
		if (
			integrationType === Api.ConfigurableIntegrationType.Clio ||
			integrationType === Api.ConfigurableIntegrationType.Redtail
		) {
			const connectedUserConfigs =
				integrationType === Api.ConfigurableIntegrationType.Clio
					? (integration as Api.IClioIntegration).integrationUserConfigurations?.filter(user => user.isConnected)
					: (integration as Api.IRedtailIntegration).redtailUserConfigurations?.filter(user => user.isConnected);

			const options = connectedUserConfigs
				?.map(u => {
					let user: ISelectOption<AccountUserViewModel>;

					this.mAccountUsers.accountUsers.map(accountUser => {
						if (accountUser.id === u.levitateUserId) {
							user = {
								dataContext: accountUser,
								id: accountUser.id,
								text: getEntityDisplayName(accountUser),
							};
						}
					});

					return user;
				})
				?.filter(o => o);

			if (!options || options?.length === 0) {
				return null;
			}

			const selectedByDefault =
				options.find(o => o.dataContext.id === clioFieldsToUpdate.primaryUser?.levitateUserId) ??
				options.find(o => o.dataContext.id === redtailFieldsToUpdate.primaryUser?.levitateUserId) ??
				options.find(o => o.dataContext.id === integration.primaryUser?.levitateUserId);

			return (
				<AccountDetailsInfoField
					className={`${css(styleSheet.infoField)}`}
					editingStyles={[styleSheet.infoFieldEditing]}
					label='Primary Owner'
					isEditMode={isEditing}
				>
					{isEditing ? (
						<Select
							value={selectedByDefault?.id}
							onValueChange={value => {
								const option = options.find(o => o.id === value);
								if (option) this.onPrimaryOwnerOptionClick(option);
							}}
						>
							<SelectTrigger styles={styleSheet.selectContainer}>
								<span>{selectedByDefault?.text}</span>
							</SelectTrigger>
							<SelectContent>
								{options.map(option => (
									<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : (
						(selectedByDefault?.text ?? '--')
					)}
				</AccountDetailsInfoField>
			);
		}

		return null;
	}

	private renderProspectOptionsField() {
		const { isEditing, showNumberOfYearsModal } = this.state;
		return (
			<>
				<AccountDetailsInfoField
					className={`${css(styleSheet.infoField)}`}
					editingStyles={[styleSheet.infoFieldEditing]}
					label='Prospect Options'
					isEditMode={false}
				>
					{isEditing ? (
						<Select
							value={this.getSelectedProspectOption()?.id}
							onValueChange={value => {
								const option = ProspectOptionsOptions.find(o => o.id === value);
								if (option) this.onProspectOptionsChanged(option);
							}}
						>
							<SelectTrigger styles={styleSheet.selectContainer}>
								<span>{this.getSelectedProspectOption()?.text}</span>
							</SelectTrigger>
							<SelectContent>
								{ProspectOptionsOptions.map(option => (
									<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : (
						this.renderProspectOptions()
					)}
				</AccountDetailsInfoField>
				<TextInputModal
					cta='Okay'
					isValid={this.isNumberOfYearsValid}
					modalProps={{
						isOpen: showNumberOfYearsModal,
						onRequestClose: this.onNumberOfYearsSelected,
					}}
					placeholderText='Number of years'
					title='Show prospects from the last X years'
				/>
			</>
		);
	}

	private getAvailable(type: RedtailAvailableOptions) {
		const { integration: i } = this.props;
		const integration = i as Api.IRedtailIntegration;

		return (
			(type === 'statuses'
				? integration.availableStatuses
				: type === 'contactTypes'
					? integration.availableContactTypes
					: integration.availableCategories) || []
		);
	}

	private getBlocked(type: RedtailAvailableOptions) {
		const { integration: i } = this.props;
		const { redtailFieldsToUpdate } = this.state;

		const integration = i as Api.IRedtailIntegration;
		const fieldsToUpdate = redtailFieldsToUpdate;

		return (
			(type === 'statuses'
				? (fieldsToUpdate.blockedStatuses ?? integration.blockedStatuses)
				: type === 'contactTypes'
					? (fieldsToUpdate.blockedContactTypes ?? integration.blockedContactTypes)
					: (fieldsToUpdate.blockedCategories ?? integration.blockedCategories)) || []
		);
	}

	private renderRedtailAvailableOptions(type: RedtailAvailableOptions) {
		const { isEditing } = this.state;
		const availableOptions = this.getAvailable(type);

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.availableOptionsInfoField)}`}
				label={`Available ${
					type === 'statuses' ? 'Statuses' : type === 'contactTypes' ? 'Contact Types' : 'Categories'
				}`}
				isEditMode={false}
			>
				{isEditing ? (
					<MultiSelect
						onClose={
							type === 'statuses'
								? this.onRedtailAvailableStatusesChange
								: type === 'contactTypes'
									? this.onRedtailAvailableContactTypesChange
									: this.onRedtailAvailableCategoriesChange
						}
						options={availableOptions.map(opt => this.renderRedtailAvailableOption(opt))}
						optionStyles={[styleSheet.selectAvailableOptionsOption]}
						selectedOptions={this.getRedtailAvailableOptions(type)}
						// @ts-ignore
						selectedOptionsTitle={this.renderAvailableOptionsTrigger}
						styles={[styleSheet.selectContainer]}
					/>
				) : (
					<div>
						{this.getRedtailAvailableOptions(type).map(opt => {
							return (
								<p className={css(styleSheet.availableOption)} key={opt.dataContext.toLowerCase().split(' ').join('-')}>
									{opt.dataContext}
								</p>
							);
						}) || 'None Selected'}
					</div>
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderRedtailAvailableOptionSection() {
		const { integration: i, integrationType } = this.props;
		const integration = i ? (i as Api.IRedtailIntegration) : null;
		return integrationType === Api.ConfigurableIntegrationType.Redtail &&
			(integration?.availableStatuses?.length || integration?.availableCategories?.length) ? (
			<div className={css(styleSheet.availableOptionsContainer)}>
				{integration?.availableStatuses?.length && (
					<div className={css(styleSheet.cardGroup, styleSheet.availableOptionsWrapper)}>
						<div className={css(styleSheet.availableOptions)}>{this.renderRedtailAvailableOptions('statuses')}</div>
					</div>
				)}
				{integration?.availableCategories?.length && (
					<div className={css(styleSheet.cardGroup, styleSheet.availableOptionsWrapper)}>
						<div className={css(styleSheet.availableOptions)}>{this.renderRedtailAvailableOptions('categories')}</div>
					</div>
				)}
				{integration?.availableContactTypes?.length && (
					<div className={css(styleSheet.cardGroup, styleSheet.availableOptionsWrapper)}>
						<div className={css(styleSheet.availableOptions)}>{this.renderRedtailAvailableOptions('contactTypes')}</div>
					</div>
				)}
			</div>
		) : null;
	}

	private renderHawksoftSubStatus() {
		const { integration: i, integrationType } = this.props;
		const { isEditing, showSubStatusNumberOfYearsModal, hawkSoftFieldsToUpdate, cancelledYears } = this.state;
		const integration = i as Api.IHawkSoftIntegration;

		if (
			integrationType !== Api.ConfigurableIntegrationType.HawkSoft ||
			!integration.tagKeyFactOptions?.subStatusTagEnabled ||
			!integration.tagKeyFactOptions?.subStatusTagOption
		) {
			return null;
		}

		const getSubstatusOption = (value: string): ISelectOption<string> => {
			return {
				dataContext: value,
				id: `substatus-option-${value}`,
				text: value,
				type: 'checkbox',
			};
		};

		const years = cancelledYears ?? integration.tagKeyFactOptions.subStatusTagOption?.cancelledYears;

		const getCancelledSubstatusOption = (): ISelectOption<string> => {
			const value = Api.SubStatusIntegration.Cancelled.toString();
			return {
				dataContext: value,
				id: `substatus-option-${value}`,
				text: years ? `${value} (${years} yrs)` : value,
				type: 'checkbox',
			};
		};

		const availableSubstatusOptions = [
			getSubstatusOption(Api.SubStatusIntegration.Active.toString()),
			getCancelledSubstatusOption(),
			getSubstatusOption(Api.SubStatusIntegration.Prospect.toString()),
		];

		const selectedOptions = availableSubstatusOptions.filter(option => {
			return (
				hawkSoftFieldsToUpdate.tagKeyFactOptions?.subStatusTagOption?.subStatusOptions ??
				integration.tagKeyFactOptions?.subStatusTagOption?.subStatusOptions
			)?.find(field => option.dataContext === field.toString());
		});

		return (
			<>
				<AccountDetailsInfoField
					className={`${css(styleSheet.infoField)}`}
					editingStyles={[styleSheet.infoFieldEditing]}
					label='Sub-Status Options'
					isEditMode={isEditing}
				>
					{isEditing ? (
						<MultiSelect
							onClose={this.onHawkSoftSubstatusOptionsClose}
							options={availableSubstatusOptions}
							// @ts-ignore
							selectedOptionsTitle={this.renderHawkSoftSubstatusOptionsTrigger}
							selectedOptions={selectedOptions}
							styles={[styleSheet.selectContainer]}
						/>
					) : integration?.tagKeyFactOptions?.subStatusTagOption?.subStatusOptions?.length > 0 ? (
						'Multiple Selected'
					) : (
						'None Selected'
					)}
				</AccountDetailsInfoField>
				<TextInputModal
					cta='Okay'
					isValid={this.isNumberOfYearsValid}
					modalProps={{
						isOpen: showSubStatusNumberOfYearsModal,
						onRequestClose: this.onNumberOfSubStatusYearsSelected,
					}}
					placeholderText='Number of years'
					title='Show cancelled policies from the last X years'
					initialValue={years ? years.toString() : ''}
				/>
			</>
		);
	}

	private renderUsersMapping() {
		const { integration: i, integrationType } = this.props;

		let userConfigurations: Api.IIntegrationUserConfiguration[];
		let integration: Api.IRedtailIntegration | Api.IClioIntegration | Api.IQQIntegration;

		if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			integration = i ? (i as Api.IClioIntegration) : null;
			userConfigurations = integration.integrationUserConfigurations;
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			integration = i ? (i as Api.IQQIntegration) : null;
			userConfigurations = integration.integrationUserConfigurations;
		} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			integration = i ? (i as Api.IRedtailIntegration) : null;
			userConfigurations = (integration as Api.IRedtailIntegration).redtailUserConfigurations;
		}

		if (
			integrationType === Api.ConfigurableIntegrationType.Redtail ||
			integrationType === Api.ConfigurableIntegrationType.Clio ||
			integrationType === Api.ConfigurableIntegrationType.QQ
		) {
			if (integration) {
				const users = this.mAccountUsers?.accountUsers?.map(user => {
					const integrationUser = userConfigurations?.filter(u => user.id === u.levitateUserId)?.[0];
					const displayName = getDisplayName(user);

					if (!displayName) {
						return null;
					}

					return (
						<div className={css(styleSheet.redtailUser)} key={uuid()}>
							<div className={css(styleSheet.redtailUserName)}>{displayName}</div>
							<div className={css(styleSheet.redtailUserStatus)}>
								{integrationUser?.isConnected ? (
									<span className={css(styleSheet.statusIndicator)}>
										<span>
											<SuccessCheckIcon className={css(styleSheet.statusIndicatorActive)} />
										</span>
										Connected
									</span>
								) : (
									<div className={css(styleSheet.statusIndicator, styleSheet.statusIndicatorInactive)}>
										<span />
										Not Connected
									</div>
								)}
							</div>
							<div className={css(styleSheet.redtailUserLastDate)}>
								{integrationUser?.lastLoadDate && (
									<span>{moment(integrationUser.lastLoadDate).format('MMM DD YYYY, h:mm a')}</span>
								)}
							</div>
							<div className={css(styleSheet.redtailUserLastDate)}>
								{integrationUser?.lastExtractDate && (
									<span>{moment(integrationUser.lastExtractDate).format('MMM DD YYYY, h:mm a')}</span>
								)}
							</div>
						</div>
					);
				});

				return (
					<>
						<div className={css(styleSheet.redtailUsersText)}>
							We&apos;ve synced your data, and found the following users on the account.
						</div>
						<div className={css(styleSheet.cardGroup, styleSheet.redtailUsersContainer)}>
							<div className={css(styleSheet.redtailUserHeaders)}>
								<div className={css(styleSheet.redtailUserHeader, styleSheet.redtailUserName)}>Name</div>
								<div className={css(styleSheet.redtailUserHeader, styleSheet.redtailUserStatus)}>Status</div>
								<div className={css(styleSheet.redtailUserHeader, styleSheet.redtailUserLastDate)}>Last Load Date</div>
								<div className={css(styleSheet.redtailUserHeader, styleSheet.redtailUserLastDate)}>
									Last Extract Date
								</div>
							</div>
							<div className={css(styleSheet.redtailUsers)}>{users}</div>
						</div>
					</>
				);
			}
		}
		return null;
	}

	private renderTagsDropdown() {
		const { integrationType } = this.props;
		const { isEditing } = this.state;
		let options: ISelectOption<ITagKeyFactOption>[] = [];

		if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			options = this.getAms360TagKeyFactOptions();
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			options = this.getHawkSoftTagKeyFactOptions();
		} else if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			options = this.getRedtailTagKeyFactOptions();
		} else if (integrationType === Api.ConfigurableIntegrationType.Eclipse) {
			options = this.getEclipseTagKeyFactOptions();
		} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			options = this.getClioTagKeyFactOptions();
		} else if (integrationType === Api.ConfigurableIntegrationType.EzLynx) {
			options = this.getEzlynxTagKeyFactOptions();
		} else if (integrationType === Api.ConfigurableIntegrationType.Epic) {
			options = this.getEpicTagKeyFactOptions();
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			options = this.getQQTagKeyFactOptions();
		} else if (integrationType === Api.ConfigurableIntegrationType.Xanatek) {
			options = this.getXanatekTagKeyFactOptions();
		} else if (integrationType === Api.ConfigurableIntegrationType.DonorPerfect) {
			options = this.getDonorPerfectTagKeyFactOptions();
		}

		if (options.length <= 0) {
			return null;
		}

		const selectedOptions = options.filter(opt => opt.dataContext.enabled);

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Tags'
				isEditMode={false}
			>
				{isEditing ? (
					<MultiSelect
						onClose={this.onTagsDropdownClose}
						options={options}
						optionStyles={[styleSheet.selectOption]}
						selectedOptions={selectedOptions}
						// @ts-ignore
						selectedOptionsTitle={this.renderTagsDropdownTrigger}
						styles={[styleSheet.selectContainer]}
					/>
				) : (
					this.renderTagsDropdownTrigger(selectedOptions)
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderTagsDropdownTrigger(selectedOptions: ISelectOption<ITagKeyFactOption>[]) {
		return selectedOptions.length > 1
			? 'Multiple Selected'
			: selectedOptions.length
				? selectedOptions[0].text
				: 'None Selected';
	}

	private renderWriteBack() {
		const { integration: i, integrationType } = this.props;
		const {
			redtailFieldsToUpdate,
			amsFieldsToUpdate,
			clioFieldsToUpdate,
			hawkSoftFieldsToUpdate,
			qqFieldsToUpdate,
			isEditing,
		} = this.state;
		let integration:
			| Api.IRedtailIntegration
			| Api.IAms360Integration
			| Api.IClioIntegration
			| Api.IQQIntegration
			| Api.IHawkSoftIntegration;
		let fieldsToUpdate:
			| Partial<Api.IRedtailIntegration>
			| Partial<Api.IAms360Integration>
			| Partial<Api.IClioIntegration>
			| Partial<Api.IQQIntegration>
			| Partial<Api.IHawkSoftIntegration>;
		const options = WriteBackOptions;

		if (integrationType === Api.ConfigurableIntegrationType.Redtail) {
			integration = i ? (i as Api.IRedtailIntegration) : null;
			fieldsToUpdate = redtailFieldsToUpdate;
		} else if (integrationType === Api.ConfigurableIntegrationType.Ams360) {
			integration = i ? (i as Api.IAms360Integration) : null;
			fieldsToUpdate = amsFieldsToUpdate;
		} else if (integrationType === Api.ConfigurableIntegrationType.Clio) {
			integration = i ? (i as Api.IClioIntegration) : null;
			fieldsToUpdate = clioFieldsToUpdate;
		} else if (integrationType === Api.ConfigurableIntegrationType.QQ) {
			integration = i ? (i as Api.IQQIntegration) : null;
			fieldsToUpdate = qqFieldsToUpdate;
		} else if (integrationType === Api.ConfigurableIntegrationType.HawkSoft) {
			integration = i ? (i as Api.IHawkSoftIntegration) : null;
			fieldsToUpdate = hawkSoftFieldsToUpdate;
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Write Back'
				isEditMode={false}
			>
				{isEditing ? (
					<MultiSelect
						onClose={this.onWriteBackChange}
						options={options}
						optionStyles={[styleSheet.selectOption]}
						selectedOptions={WriteBackOptions.filter(opt =>
							(fieldsToUpdate?.writeOptions?.writeBackTypes ?? integration?.writeOptions?.writeBackTypes)?.includes(
								opt.dataContext
							)
						)}
						selectedOptionsTitle={this.renderWriteBackTrigger}
						styles={[styleSheet.selectContainer]}
					/>
				) : (
					WriteBackOptions.filter(opt => integration.writeOptions?.writeBackTypes?.includes(opt.dataContext))
						.map(opt => opt.text)
						.join(' and ') || 'None Selected'
				)}
			</AccountDetailsInfoField>
		);
	}

	private renderIgnoreNicknames() {
		const { integration: i, integrationType } = this.props;
		const { ezLynxFieldsToUpdate, epicFieldsToUpdate, xanatekFieldsToUpdate, isEditing } = this.state;
		let integration: Api.IEzLynxCsvIntegration | Api.IEpicCsvIntegration | Api.IGeneralCsvIntegration;
		let fieldsToUpdate:
			| Partial<Api.IEzLynxCsvIntegration>
			| Partial<Api.IEpicCsvIntegration>
			| Partial<Api.IGeneralCsvIntegration>;

		switch (integrationType) {
			case Api.ConfigurableIntegrationType.EzLynx:
				integration = i ? (i as Api.IEzLynxCsvIntegration) : null;
				fieldsToUpdate = ezLynxFieldsToUpdate;
				break;
			case Api.ConfigurableIntegrationType.Epic:
				integration = i ? (i as Api.IEpicCsvIntegration) : null;
				fieldsToUpdate = epicFieldsToUpdate;
				break;
			case Api.ConfigurableIntegrationType.Xanatek:
				integration = i ? (i as Api.IGeneralCsvIntegration) : null;
				fieldsToUpdate = xanatekFieldsToUpdate;
				break;
			default:
				break;
		}

		return (
			<AccountDetailsInfoField
				className={`${css(styleSheet.infoField)}`}
				editingStyles={[styleSheet.infoFieldEditing]}
				label='Ignore Nicknames'
				isEditMode={isEditing}
			>
				{isEditing ? (
					<Select
						value={
							EnableOptions[(fieldsToUpdate.ignoreContactNicknames ?? integration.ignoreContactNicknames) ? 0 : 1]?.id
						}
						onValueChange={value => {
							const option = EnableOptions.find(o => o.id === value);
							if (option) this.onIgnoreNicknamesOptionChange(integrationType)(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectContainer}>
							<span>
								{
									EnableOptions[(fieldsToUpdate.ignoreContactNicknames ?? integration.ignoreContactNicknames) ? 0 : 1]
										?.text
								}
							</span>
						</SelectTrigger>
						<SelectContent>
							{EnableOptions.map(option => (
								<SelectItem key={option.id} value={option.id} styles={styleSheet.selectOption}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					EnableOptions[(fieldsToUpdate.ignoreContactNicknames ?? integration.ignoreContactNicknames) ? 0 : 1].text
				)}
			</AccountDetailsInfoField>
		);
	}

	private loadTags() {
		const { errorMessages, integrationType, integration } = this.props;

		if (integration?.configured) {
			const integrationVm = this.mIntegrationsVM.getByType(integrationType);
			integrationVm?.setIntegration(integration);
			integrationVm?.getTags()?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
			});
		}
	}
}

const ObservableAdminConfigurableIntegrationSettings = observer(_AdminConfigurableIntegrationSettings);
export const AdminConfigurableIntegrationSettings = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey,
	ImpersonationContextKey
)(ObservableAdminConfigurableIntegrationSettings);
